import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MemberShell from "@/components/MemberShell";
import DashboardPriorityBanner from "@/components/DashboardPriorityBanner";
import { buildMemberDashboardNav, DASH_OVERVIEW_TAB, MEMBER_DASH_TAB_ICONS } from "@/lib/memberDashboardNav";
import { MEMBER_GLOBAL_NAV_ICONS } from "@/lib/navIcons";
import {
  findDepartementFieldRaw,
  formatDepartmentDisplay,
  getDeptDisplayLabel,
  isDepartementChampKey,
} from "@/lib/departementDisplay";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { subscribeDossierCallLock } from "@/lib/realtimeCallLock";
import { supabase } from "@/lib/supabase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ListShell, ListRow, DetailDialog } from "@/components/shared/ListDetail";
import ModalDossierEmbed from "@/components/shared/ModalDossierEmbed";
import { ModalSummaryBar, ModalSection, ModalContactCard, ModalActions } from "@/components/shared/ModalSections";
import TransactionTrackingCard from "@/components/shared/TransactionTrackingCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import RatingStars from "@/components/RatingStars";
import StripePaymentDialog from "@/components/StripePaymentDialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { recoverSubscription, waitForActiveSubscription } from "@/lib/subscriptionActivation";
import { useMemberSellerWallet } from "@/hooks/useMemberSellerWallet";
import { isLitigeRowOpen } from "@/lib/buyerPrivateAccess";
import SubscriptionRequiredPurchaseModal from "@/components/SubscriptionRequiredPurchaseModal";
import { isSubscriptionRequiredError } from "@/lib/subscriptionRequired";
import { startSubscriptionCheckout } from "@/lib/subscriptionCheckout";
import { handleAcheterResponse, postDossierBuyParallel } from "@/lib/dossierBuyCheckout";
import { formatPhoneForDisplay, formatPhoneInputLive, isPhoneLikeChampName } from "@/lib/phoneFormat";
import { phoneNormalizationError } from "@/lib/dossierPhoneNormalize";
import {
  Phone,
  Star,
  Scale,
  Gavel,
  FileText,
  Download,
  Share2,
  Pencil,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SELLER_PAYOUT_HOURS } from "@/lib/businessDays";
import { buyerPrivateUnlocked, normTxId } from "@/lib/buyerPrivateAccess";
import { matchListSearch } from "@/lib/listSearch";
import { resolveTransactionTracking } from "@/lib/transactionTracking";
import TrackingListSubline from "@/components/shared/TrackingListSubline";
import { hasBlockingPurchaseForDossier } from "@/lib/transactionPurchaseGuard";
import { BuyerCallSession } from "@/components/BuyerCallSession";
import {
  isDisputeOpeningBlockedByPlatform,
  OPEN_DISPUTE_BLOCKED_PLATFORM_DETAIL_FR,
} from "@/lib/disputeEligibility";
import { litigeDecisionExplainedFr } from "@/lib/litigeDecisionLabels";
import {
  annonceDossierStatusBadge,
  litigeRowStatusLabelFr,
  negotiationStatusLabelFr,
} from "@/lib/marketplaceStatusLabels";
import DeptLocationPills from "@/components/DeptLocationPills";

const statusLabels = {
  en_attente: { label: "Paiement à valider", className: "bg-slate-200 text-slate-700" },
  paye: { label: "Séquestre (payé)", className: "bg-blue-100 text-blue-800" },
  appel_en_cours: { label: "Appel en cours", className: "bg-red-100 text-red-800" },
  appel_effectue: { label: "Décision requise", className: "bg-amber-100 text-amber-800" },
  fonds_debloques: { label: "Gain validé", className: "bg-emerald-50 text-emerald-900 border border-emerald-200" },
  litige: { label: "Litige", className: "bg-orange-100 text-orange-800" },
  rembourse: { label: "Remboursé", className: "bg-slate-200 text-slate-700" },
  annule: { label: "Annulé", className: "bg-slate-200 text-slate-700" },
};
const DASHBOARD_SEEN_TABS_KEY = "dashboard_seen_tabs_v1";
const DASHBOARD_ACTIVE_TAB_KEY = "dashboard_active_tab_v1";
const DASHBOARD_WORK_TABS = ["buys", "sales", "annonces", "suggestions", "negotiations", "disputes", "invoices"];
const ALL_DASHBOARD_TABS = [DASH_OVERVIEW_TAB, ...DASHBOARD_WORK_TABS];

/** Au-dessus des DetailDialog Radix (z-50) ; sous les sous-modales BuyerCallSession embarqué (z-120+). */
const RDM_CALL_SHELL_CLASS =
  "pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overflow-x-hidden p-3 sm:p-6";

const normalizeAnnonceType = (raw) => {
  const t = String(raw || "").toLowerCase().trim();
  if (t === "recherche" || t === "achat" || t === "buy") return "recherche";
  if (t === "vente" || t === "offre" || t === "sell") return "vente";
  return t;
};

const normalizeChampKey = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");

const extractDeptFromText = (text) => {
  const t = String(text || "").trim();
  if (!t) return null;
  if (/^\d{2,3}$/.test(t)) return t;
  const m = t.match(/\b(\d{5})\b/);
  if (!m) return null;
  const cp = m[1];
  if (cp.startsWith("97") || cp.startsWith("98")) return cp.slice(0, 3);
  return cp.slice(0, 2);
};

/** Horodatage de clôture RDV pour la fenêtre litige (aligné SELLER_PAYOUT_HOURS / capture_confirmed_at). */
function disputeAnchorMs(tx) {
  const raw = tx?.capture_confirmed_at;
  if (!raw) return null;
  const ms = Date.parse(String(raw));
  return Number.isFinite(ms) ? ms : null;
}

/** Achat : fenêtre litige ouverte, pas de litige déjà déposé, plateforme ne bloque pas. */
function buyCanUserOpenDispute(tx, disputesRows) {
  const txKey = normTxId(tx?.transaction_id ?? tx?.id);
  const litigesForT = (disputesRows || []).filter((d) => normTxId(d?.transaction_id) === txKey);
  if (litigesForT.length > 0) return false;
  if (!isBuyerDisputeWindowOpen(tx)) return false;
  return !isDisputeOpeningBlockedByPlatform(tx);
}

/** Bouton « ouvrir litige » : fonds débloqués ou remboursé, dans les N heures après l’ancre (pas si déjà en litige). */
function isBuyerDisputeWindowOpen(tx, hours = SELLER_PAYOUT_HOURS) {
  if (Number(hours) <= 0) return true;
  const st = String(tx?.statut || "");
  if (st === "litige") return false;
  if (st !== "fonds_debloques" && st !== "rembourse") return false;
  const anchor = disputeAnchorMs(tx);
  if (anchor == null) return true;
  return Date.now() <= anchor + Number(hours) * 3600 * 1000;
}

/** Dernier litige clos par transaction (horodatage `resolved_at` / mises à jour). */
function latestClosedDisputeByTxMap(disputesRows) {
  const list = Array.isArray(disputesRows) ? disputesRows : [];
  const m = new Map();
  for (const d of list) {
    if (String(d?.status ?? d?.statut ?? "").toLowerCase() !== "closed") continue;
    const tid = normTxId(d?.transaction_id);
    if (!tid) continue;
    const ts = Date.parse(String(d.resolved_at || d.updated_at || d.created_at || "")) || 0;
    const prev = m.get(tid);
    if (!prev || ts >= (prev._ts || 0)) m.set(tid, { ...d, _ts: ts });
  }
  return m;
}

/** Message plateforme affiché uniquement à la partie perdante après clôture admin. */
function disputeLoserNotice(userId, tx, litRow) {
  if (!litRow || !userId) return null;
  const dec = String(litRow.admin_decision || "").toLowerCase();
  if (dec !== "refund" && dec !== "release") return null;
  const buyerId = litRow.buyer_id ?? tx?.buyer_id;
  const sellerId = litRow.seller_id ?? tx?.seller_id;
  const loser = dec === "refund" ? sellerId : buyerId;
  if (String(loser || "") !== String(userId)) return null;
  const msg = String(litRow.resolution_message || "").trim();
  const headline =
    dec === "refund"
      ? "Litige clos : décision en faveur de l’acheteur."
      : "Litige clos : décision en faveur du vendeur.";
  return { headline, message: msg, adminDecision: dec };
}

function disputeWinnerLabel(userId, tx, litRow) {
  if (!litRow || !userId) return null;
  const dec = String(litRow.admin_decision || "").toLowerCase();
  if (dec !== "refund" && dec !== "release") return null;
  const buyerId = litRow.buyer_id ?? tx?.buyer_id;
  const sellerId = litRow.seller_id ?? tx?.seller_id;
  const winner = dec === "refund" ? buyerId : sellerId;
  if (String(winner || "") !== String(userId)) return null;
  return "Litige statué en votre faveur";
}

/** Après litige clos en faveur de l’acheteur (`refund`), la ligne ne figure plus que dans l’onglet Litiges. */
/** Titre annonce pour un litige (API enrichie ou repli achats/ventes). */
function disputeListingTitle(d, buys, sales) {
  if (d?.dossier_title) return d.dossier_title;
  const txId = normTxId(d?.transaction_id);
  const tx =
    (txId && buys.find((b) => normTxId(b?.transaction_id ?? b?.id) === txId)) ||
    (txId && sales.find((s) => normTxId(s?.transaction_id ?? s?.id) === txId)) ||
    null;
  if (tx?.dossier_title) return tx.dossier_title;
  const did = d?.dossier_id ?? tx?.dossier_id;
  if (did) return `Annonce ${String(did).slice(0, 8)}…`;
  return "Annonce non identifiée";
}

function hideBuyerPurchaseFromMesAchatsTab(userId, tx, closedLitByTx) {
  if (!userId || !tx || !closedLitByTx || typeof closedLitByTx.get !== "function") return false;
  const txKey = normTxId(tx.transaction_id ?? tx.id);
  if (!txKey) return false;
  const lit = closedLitByTx.get(txKey);
  if (!lit) return false;
  const dec = String(lit.admin_decision || "").toLowerCase();
  if (dec !== "refund") return false;
  const buyerId = lit.buyer_id ?? tx.buyer_id;
  return String(buyerId || "") === String(userId);
}

const extractPostalCode = (text) => {
  const t = String(text || "").trim();
  if (!t) return null;
  const m = t.match(/\b(\d{4,5})\b/);
  return m ? m[1] : null;
};

const extractCountryCode = (dossier) => {
  const cf =
    dossier?.custom_fields && typeof dossier.custom_fields === "object" && !Array.isArray(dossier.custom_fields)
      ? dossier.custom_fields
      : {};
  for (const k of Object.keys(cf)) {
    const nk = normalizeChampKey(k);
    if (nk === "pays" || nk === "country") {
      const v = String(cf[k] || "").trim().toUpperCase();
      if (v === "FR" || v === "FRANCE") return "FR";
      if (v === "BE" || v.includes("BELGI")) return "BE";
      if (v === "CH" || v.includes("SUIS")) return "CH";
    }
  }
  return "FR";
};

const getGeoLabel = (dossier) => {
  const country = extractCountryCode(dossier);
  if (country === "FR") {
    const dept = getDeptDisplayLabel(dossier);
    return dept ? { kind: "dept", value: dept } : null;
  }
  const cf =
    dossier?.custom_fields && typeof dossier.custom_fields === "object" && !Array.isArray(dossier.custom_fields)
      ? dossier.custom_fields
      : {};
  for (const k of Object.keys(cf)) {
    const nk = normalizeChampKey(k);
    if (nk.includes("code postal") || nk.includes("postal") || nk.includes("adresse") || nk.includes("address")) {
      const cp = extractPostalCode(cf[k]);
      if (cp) return { kind: "postal", value: cp };
    }
  }
  const cp = extractPostalCode(dossier?.location);
  return cp ? { kind: "postal", value: cp } : null;
};

/** Libellé encaissement vendeur (délai 72 h + litige). */
function sellerPayoutScheduleHint(tx) {
  if (!tx?.capture_confirmed_at || tx.statut === "rembourse") return null;
  const onHold = Boolean(tx.seller_payout_on_hold);
  const availRaw = tx.seller_payout_available_at;
  const availTs = availRaw ? Date.parse(availRaw) : NaN;
  const n = SELLER_PAYOUT_HOURS;
  if (onHold) {
    return "Versement suspendu : litige en cours sur cette vente.";
  }
  if (Number.isFinite(availTs)) {
    const d = new Date(availTs);
    const passed = Date.now() >= availTs;
    const dateStr = d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
    if (passed) {
      return `Délai de sécurité acheteur écoulé (≥ ${dateStr}, ${n} h après validation du RDV). Versement selon Stripe Connect.`;
    }
    return `Encaissement vendeur estimé à partir du ${dateStr} (${n} h après validation du RDV).`;
  }
  return null;
}

function sellerPayoutTransferred(tx) {
  return Boolean(tx?.stripe_connect_transfer_id && String(tx.stripe_connect_transfer_id).trim());
}

/**
 * Libellé de statut côté **vendeur** (Mes ventes).
 * « Gain validé » ≠ argent sur le compte bancaire : le versement Stripe nécessite identité + IBAN.
 */
function sellerSaleHasOpenDispute(tx) {
  return Boolean(tx?.seller_payout_on_hold) || (tx?.litiges || []).some(isLitigeRowOpen);
}

function sellerSaleStatusBadge(tx, { payoutsReady = true } = {}) {
  const s = tx?.statut;
  if (s === "fonds_debloques") {
    if (sellerPayoutTransferred(tx)) {
      return { label: "Versé", className: "bg-emerald-100 text-emerald-800" };
    }
    if (sellerSaleHasOpenDispute(tx)) {
      return { label: "Litige en cours", className: "bg-orange-100 text-orange-900 border border-orange-200" };
    }
    if (!payoutsReady) {
      return { label: "IBAN requis", className: "bg-amber-100 text-amber-900 border border-amber-200" };
    }
    if (tx?.tracking?.payout_due || tx?.payout_due) {
      return { label: "Versement en cours", className: "bg-sky-50 text-sky-900 border border-sky-200" };
    }
    return statusLabels.fonds_debloques;
  }
  if (s === "litige") return statusLabels.litige;
  if (s === "rembourse") return statusLabels.rembourse;
  if (s === "annule") return statusLabels.annule;
  return { label: "En attente", className: "bg-amber-50 text-amber-900 border border-amber-200" };
}

const isContactLikeChamp = (nom) => {
  const k = normalizeChampKey(nom);
  return (
    k === "nom du client" ||
    k === "nom du client/prospect" ||
    k === "telephone du client" ||
    k === "telephone du client/prospect" ||
    k === "tel du client" ||
    k === "adresse du client" ||
    k === "adresse client" ||
    k === "departement" ||
    k === "département" ||
    k === "pays" ||
    k === "localisation" ||
    k === "location"
  );
};

/** Coordonnées prospect — masquées sur les annonces « recherche ». */
const isClientIdentityChamp = (nom) => {
  const k = normalizeChampKey(nom);
  return (
    k === "nom du client" ||
    k === "nom du client/prospect" ||
    k === "telephone du client" ||
    k === "telephone du client/prospect" ||
    k === "tel du client" ||
    k === "adresse du client" ||
    k === "adresse client"
  );
};

/** Boutons Marketplace + Publier (vue d’ensemble, achats, ventes). */
function DashboardMarketplaceStrip({ testIdPrefix = "dash", className }) {
  return (
    <div
      className={cn(
        "mb-4 flex flex-col gap-2 rounded-xl border border-slate-200/90 bg-gradient-to-br from-slate-50/80 to-white p-3 sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:p-4",
        className,
      )}
      data-testid={`${testIdPrefix}-marketplace-strip`}
    >
      <p className="text-sm font-medium text-slate-600 sm:mr-auto sm:max-w-md">
        Parcourir la marketplace ou publier une annonce.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:shrink-0">
        <Button
          asChild
          className="btn-primary h-10 w-full rounded-xl px-4 font-semibold shadow-sm shadow-[#2D7FF9]/20 sm:w-auto"
          data-testid={`${testIdPrefix}-go-marketplace`}
        >
          <Link to="/marketplace" className="inline-flex items-center whitespace-normal">
            <MEMBER_GLOBAL_NAV_ICONS.marketplace className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            Marketplace
          </Link>
        </Button>
        <Button
          asChild
          variant="outline"
          className="h-10 w-full rounded-xl border-slate-200 bg-white px-4 font-semibold hover:border-[#2D7FF9]/40 hover:bg-[#2D7FF9]/5 sm:w-auto"
          data-testid={`${testIdPrefix}-go-publish`}
        >
          <Link to="/nouveau-dossier" className="inline-flex items-center whitespace-normal">
            <MEMBER_GLOBAL_NAV_ICONS.publish className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            Publier une annonce
          </Link>
        </Button>
      </div>
    </div>
  );
}

const Dashboard = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /** Entrée « tableau de bord » sans onglet → marketplace (ex. avatar homepage). Vue d’ensemble : ?tab=overview */
  useLayoutEffect(() => {
    const tab = new URLSearchParams(location.search || "").get("tab");
    if (!tab) {
      navigate("/marketplace", { replace: true });
    }
  }, [location.search, navigate]);

  const prenom = useMemo(() => {
    const fn = user?.first_name || user?.prenom || user?.prénom;
    if (fn && String(fn).trim()) return String(fn).trim();
    const n = user?.name;
    if (!n || !String(n).trim()) return "";
    return String(n).trim().split(/\s+/)[0] || "";
  }, [user]);
  const [buys, setBuys] = useState([]);
  const [sales, setSales] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      const fromUrl = sp.get("tab");
      if (!fromUrl || fromUrl === DASH_OVERVIEW_TAB) return DASH_OVERVIEW_TAB;
      if (DASHBOARD_WORK_TABS.includes(fromUrl)) return fromUrl;
      const fromStorage = window.localStorage.getItem(DASHBOARD_ACTIVE_TAB_KEY);
      if (fromStorage && DASHBOARD_WORK_TABS.includes(fromStorage)) return fromStorage;
    } catch {
      // ignore
    }
    return DASH_OVERVIEW_TAB;
  });
  const [seenByTab, setSeenByTab] = useState(() => {
    try {
      const raw = window.localStorage.getItem(DASHBOARD_SEEN_TABS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  });
  const [myAnnonces, setMyAnnonces] = useState([]);
  const [annonceFilter, setAnnonceFilter] = useState("vente"); // "vente" | "recherche"
  /** Filtre liste Mes ventes : tout / pipeline / gains validés (pas encore versés) / déjà versés. */
  const [salesListFilter, setSalesListFilter] = useState("toutes"); // "toutes" | "en_cours" | "a_recevoir" | "versees"
  const sellerWallet = useMemberSellerWallet();
  const payoutsReady = sellerWallet.payoutsReady;
  const [negotiations, setNegotiations] = useState([]);
  /** Propositions de vendeurs sur vos annonces « recherche » (rôle acheteur annonce). */
  const [rechercheProposals, setRechercheProposals] = useState([]);
  /** Après acceptation d’une suggestion, propose le choix : acheter vs négocier. */
  const [proposalChoice, setProposalChoice] = useState(null); // { open: boolean, rechercheId: string, venteId: string, negotiationId: string }
  const [disputes, setDisputes] = useState([]);
  const [expandedUserDisputeKeys, setExpandedUserDisputeKeys] = useState(() => new Set());
  const [dashDisputeDetailKey, setDashDisputeDetailKey] = useState(null);
  const [dashInvoiceNumber, setDashInvoiceNumber] = useState(null);
  const [dashProposalId, setDashProposalId] = useState(null);
  const [dashNegDetailId, setDashNegDetailId] = useState(null);
  const [dashAnnonceDetailId, setDashAnnonceDetailId] = useState(null);
  const [dashBuyDetailTx, setDashBuyDetailTx] = useState(null);
  const [dashBuyPane, setDashBuyPane] = useState("main");
  const [buyDetailDossier, setBuyDetailDossier] = useState(null);
  const [buyDetailDossierLoading, setBuyDetailDossierLoading] = useState(false);
  const [dashDisputePane, setDashDisputePane] = useState("main");
  const [dashDisputeDossierId, setDashDisputeDossierId] = useState(null);
  const [dashAnnoncePane, setDashAnnoncePane] = useState("main");
  const [dashProposalPane, setDashProposalPane] = useState("main");
  const [dashProposalDossierId, setDashProposalDossierId] = useState(null);
  const [dashSaleDetailTx, setDashSaleDetailTx] = useState(null);
  const [dashSalePane, setDashSalePane] = useState("main");
  const [dashNegPane, setDashNegPane] = useState("main");
  const [dashBuysQuery, setDashBuysQuery] = useState("");
  const [dashSalesQuery, setDashSalesQuery] = useState("");
  const [dashAnnoncesQuery, setDashAnnoncesQuery] = useState("");
  const [dashSuggestionsQuery, setDashSuggestionsQuery] = useState("");
  const [dashNegotiationsQuery, setDashNegotiationsQuery] = useState("");
  const [dashDisputesQuery, setDashDisputesQuery] = useState("");
  const [dashInvoicesQuery, setDashInvoicesQuery] = useState("");
  const toggleUserDisputeKey = useCallback((key) => {
    const k = String(key);
    setExpandedUserDisputeKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }, []);
  const [showRating, setShowRating] = useState(null);
  /** Transactions pour lesquelles l’utilisateur connecté a déjà laissé une note (acheteur → vendeur ou vendeur → acheteur). */
  const [myRatedTxIds, setMyRatedTxIds] = useState(() => new Set());
  /** Parcours appel inline (même UI que /call/:id) dans une modale. */
  const [inlineCallTx, setInlineCallTx] = useState(null);
  const [inlineCallOpenOutcome, setInlineCallOpenOutcome] = useState(false);
  const [inlineCallForceRetry, setInlineCallForceRetry] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [negOfferAmount, setNegOfferAmount] = useState({});
  const [sendingNegOfferId, setSendingNegOfferId] = useState(null);
  const [payingNegId, setPayingNegId] = useState(null);
  const [deleteNegDialog, setDeleteNegDialog] = useState({ open: false, neg: null });
  const [deletingNegId, setDeletingNegId] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    clientSecret: "",
    txId: "",
    dossierId: "",
    negId: "",
    initialPaymentMethods: null,
  });
  const [subscribeDialog, setSubscribeDialog] = useState({ open: false, plan: "monthly", dossierId: "", negId: "" });
  const [subscribing, setSubscribing] = useState(false);
  const [subscriptionPay, setSubscriptionPay] = useState({ open: false, clientSecret: "", intentType: "payment", amountDue: null, currency: "eur", plan: "monthly", planAmount: null, planCurrency: "eur", nextAction: null, paymentIntentId: "" });
  const [pendingBuy, setPendingBuy] = useState(null); // { dossierId, negId }
  const [subscriptionSyncDialog, setSubscriptionSyncDialog] = useState({
    open: false,
    plan: "monthly",
    paymentIntentId: "",
    syncing: false,
    pendingBuySnapshot: null,
  });
  const [buyerCancelBusyId, setBuyerCancelBusyId] = useState(null);

  const [showDispute, setShowDispute] = useState(null);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeDesc, setDisputeDesc] = useState("");
  const [verdictNoticeDialog, setVerdictNoticeDialog] = useState(null);
  const consumedOpenDisputeParam = useRef(null);
  const [invoices, setInvoices] = useState([]);
  /** true = un autre acheteur a appel_en_cours sur le même dossier (pas la ligne courante). */
  const [callLockByTx, setCallLockByTx] = useState({});
  const [annoncesLoaded, setAnnoncesLoaded] = useState(false);
  const [annoncesLoadError, setAnnoncesLoadError] = useState("");
  const [editingAnnonce, setEditingAnnonce] = useState(null);
  const [editAnnonceForm, setEditAnnonceForm] = useState({
    type: "vente",
    secteur: "",
    produit_id: "",
    title: "",
    description: "",
    location: "",
    client_name: "",
    client_phone: "",
    price: "",
    price_min: "",
    price_max: "",
    custom_fields: {},
  });
  const [savingAnnonce, setSavingAnnonce] = useState(false);
  const [deletingAnnonceId, setDeletingAnnonceId] = useState(null);
  const [editProduits, setEditProduits] = useState([]);
  const asArray = (v) => (Array.isArray(v) ? v : []);

  /** Ventes côté vendeur : pas encore clôturées (séquestre, appel, litige, etc. — tout sauf encaissé / remboursé / annulé). */
  const sellerSalesOpenCount = useMemo(
    () =>
      sales.filter((t) => {
        const st = String(t?.statut || "");
        return st && !["fonds_debloques", "rembourse", "annule"].includes(st);
      }).length,
    [sales],
  );

  const sellerSalesToReceiveCount = useMemo(
    () =>
      sales.filter(
        (t) => String(t?.statut || "") === "fonds_debloques" && !sellerPayoutTransferred(t),
      ).length,
    [sales],
  );

  const sellerSalesPaidOutCount = useMemo(
    () =>
      sales.filter(
        (t) => String(t?.statut || "") === "fonds_debloques" && sellerPayoutTransferred(t),
      ).length,
    [sales],
  );

  const sellerPendingPayoutEur = sellerWallet.pendingEur;
  const sellerDisputeHoldEur = sellerWallet.disputeHoldEur;

  const filteredSales = useMemo(() => {
    if (salesListFilter === "toutes") return sales;
    if (salesListFilter === "a_recevoir" || salesListFilter === "encaisse") {
      return sales.filter(
        (t) => String(t?.statut || "") === "fonds_debloques" && !sellerPayoutTransferred(t),
      );
    }
    if (salesListFilter === "versees") {
      return sales.filter(
        (t) => String(t?.statut || "") === "fonds_debloques" && sellerPayoutTransferred(t),
      );
    }
    return sales.filter((t) => {
      const st = String(t?.statut || "");
      return st && !["fonds_debloques", "rembourse", "annule"].includes(st);
    });
  }, [sales, salesListFilter]);

  const latestClosedDisputeByTx = useMemo(() => latestClosedDisputeByTxMap(disputes), [disputes]);

  const displayBuys = useMemo(() => {
    const uid = user?.user_id;
    if (!uid) return buys;
    return buys.filter((t) => !hideBuyerPurchaseFromMesAchatsTab(uid, t, latestClosedDisputeByTx));
  }, [buys, user?.user_id, latestClosedDisputeByTx]);

  const displayBuysFiltered = useMemo(() => {
    if (!dashBuysQuery.trim()) return displayBuys;
    return displayBuys.filter((t) => {
      const st = statusLabels[t.statut] || {};
      return matchListSearch(
        dashBuysQuery,
        t.dossier_title,
        t.dossier_location,
        t.statut,
        st.label,
        t.buyer_total,
        t.transaction_id ?? t.id,
        new Date(t.created_at).toLocaleDateString("fr-FR"),
      );
    });
  }, [displayBuys, dashBuysQuery]);

  const filteredSalesSearched = useMemo(() => {
    if (!dashSalesQuery.trim()) return filteredSales;
    return filteredSales.filter((t) => {
      const st = sellerSaleStatusBadge(t, { payoutsReady });
      const amount = t.seller_payout ?? t.price ?? t.buyer_total;
      return matchListSearch(
        dashSalesQuery,
        t.dossier_title,
        t.statut,
        st.label,
        amount,
        t.transaction_id ?? t.id,
        new Date(t.created_at).toLocaleDateString("fr-FR"),
      );
    });
  }, [filteredSales, dashSalesQuery, payoutsReady]);

  const myAnnoncesByType = useMemo(
    () => myAnnonces.filter((d) => normalizeAnnonceType(d?.type) === annonceFilter),
    [myAnnonces, annonceFilter],
  );

  const myAnnoncesFiltered = useMemo(() => {
    if (!dashAnnoncesQuery.trim()) return myAnnoncesByType;
    return myAnnoncesByType.filter((d) =>
      matchListSearch(
        dashAnnoncesQuery,
        d.titre,
        d.title,
        d.location,
        d.status,
        d.statut,
        d.secteur,
        d.dossier_id,
        d.price,
      ),
    );
  }, [myAnnoncesByType, dashAnnoncesQuery]);

  const rechercheProposalsFiltered = useMemo(() => {
    if (!dashSuggestionsQuery.trim()) return rechercheProposals;
    return rechercheProposals.filter((pr) => {
      const rechercheTitle = pr?.recherche?.titre || pr?.recherche?.title || pr?.recherche_dossier_id;
      const venteTitle = pr?.vente?.titre || pr?.vente?.title || pr?.vente_dossier_id;
      return matchListSearch(dashSuggestionsQuery, rechercheTitle, venteTitle, pr.status, pr.proposal_id);
    });
  }, [rechercheProposals, dashSuggestionsQuery]);

  const negotiationsFiltered = useMemo(() => {
    if (!dashNegotiationsQuery.trim()) return negotiations;
    return negotiations.filter((n) => {
      const dossier = n?.dossiers || n?.dossier || null;
      const dossierTitle = n?.dossier_title ?? dossier?.title ?? dossier?.titre;
      const lastOffer = n.offers?.[n.offers.length - 1];
      return matchListSearch(
        dashNegotiationsQuery,
        dossierTitle,
        n.status,
        negotiationStatusLabelFr(n.status),
        lastOffer?.amount,
        n.negotiation_id,
      );
    });
  }, [negotiations, dashNegotiationsQuery]);

  const disputesFiltered = useMemo(() => {
    if (!dashDisputesQuery.trim()) return disputes;
    return disputes.filter((d) => {
      const txId = normTxId(d?.transaction_id);
      const tx =
        (txId && buys.find((b) => normTxId(b?.transaction_id ?? b?.id) === txId)) ||
        (txId && sales.find((s) => normTxId(s?.transaction_id ?? s?.id) === txId)) ||
        null;
      return matchListSearch(
        dashDisputesQuery,
        d.dossier_title,
        tx?.dossier_title,
        d.reason,
        d.description,
        d.status ?? d.statut,
        litigeRowStatusLabelFr(d?.status ?? d?.statut ?? "open"),
        d.dispute_id ?? d.id,
      );
    });
  }, [disputes, dashDisputesQuery, buys, sales]);

  const invoicesFiltered = useMemo(() => {
    if (!dashInvoicesQuery.trim()) return invoices;
    return invoices.filter((inv) =>
      matchListSearch(
        dashInvoicesQuery,
        inv.label,
        inv.invoice_number,
        inv.kind,
        inv.amount_ht,
        inv.transaction_id,
        new Date(inv.date).toLocaleDateString("fr-FR"),
      ),
    );
  }, [invoices, dashInvoicesQuery]);

  /** Recalcul périodique pour masquer le bouton litige dès la fin des 72 h sans recharger la page. */
  const [, bumpDisputeUi] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => bumpDisputeUi((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const refreshNegotiations = useCallback(async () => {
    try {
      const res = await api.get("/negotiations/mine");
      setNegotiations(asArray(res?.data));
    } catch {
      // pas de toast : refresh silencieux
    }
  }, []);

  const refreshMyRatedTxIds = useCallback(async (transactionRows) => {
    const list = Array.isArray(transactionRows) ? transactionRows : [];
    const txIds = [...new Set(list.map((x) => normTxId(x?.transaction_id ?? x?.id)).filter(Boolean))];
    if (!txIds.length) {
      setMyRatedTxIds(new Set());
      return;
    }
    try {
      const {
        data: { user: u0 },
      } = await supabase.auth.getUser();
      if (!u0?.id) return;
      const { data: notes, error: ne } = await supabase
        .from("notations")
        .select("transaction_id")
        .eq("author_id", u0.id)
        .in("transaction_id", txIds);
      if (ne) {
        console.warn("notations (transactions déjà notées par moi):", ne.message);
        return;
      }
      setMyRatedTxIds(new Set((notes || []).map((n) => normTxId(n.transaction_id)).filter(Boolean)));
    } catch (e) {
      console.warn("refreshMyRatedTxIds", e);
    }
  }, []);

  useEffect(() => {
    const buyIds = (Array.isArray(buys) ? buys : []).map((x) => normTxId(x?.transaction_id ?? x?.id)).filter(Boolean);
    const sellIds = (Array.isArray(sales) ? sales : []).map((x) => normTxId(x?.transaction_id ?? x?.id)).filter(Boolean);
    const merged = [...new Set([...buyIds, ...sellIds])].map((id) => ({ transaction_id: id }));
    void refreshMyRatedTxIds(merged);
  }, [buys, sales, refreshMyRatedTxIds]);

  const load = useCallback(async () => {
    setAnnoncesLoadError("");
    const results = await Promise.allSettled([
      api.get("/transactions/mine", { params: { as_role: "buyer" } }),
      api.get("/transactions/mine", { params: { as_role: "seller" } }),
      api.get("/dossiers/mine"),
      api.get("/negotiations/mine"),
      api.get("/disputes/mine"),
      api.get("/invoices/mine"),
      api.get("/recherche-proposals/mine", { params: { role: "buyer" } }),
    ]);

    const [b, s, d, n, dis, inv, prop] = results;
    if (b.status === "fulfilled") {
      const rawB = b.value?.data;
      const arrB = asArray(Array.isArray(rawB) ? rawB : rawB?.data ?? rawB);
      setBuys(arrB);
    }
    if (s.status === "fulfilled") {
      const rawS = s.value?.data;
      setSales(asArray(Array.isArray(rawS) ? rawS : rawS?.data ?? rawS));
    }
    if (b.status !== "fulfilled" || s.status !== "fulfilled") {
      const msgB =
        b.status !== "fulfilled"
          ? b.reason?.response?.data?.detail || b.reason?.message || "achats"
          : null;
      const msgS =
        s.status !== "fulfilled"
          ? s.reason?.response?.data?.detail || s.reason?.message || "ventes"
          : null;
      if (b.status !== "fulfilled") console.warn("transactions/mine buyer:", b.reason);
      if (s.status !== "fulfilled") console.warn("transactions/mine seller:", s.reason);
      if (msgB && msgS && String(msgB) === String(msgS)) toast.error(String(msgB));
      else {
        if (msgB) toast.error(`Mes achats : ${String(msgB)}`);
        if (msgS) toast.error(`Mes ventes : ${String(msgS)}`);
      }
    }
    if (n.status === "fulfilled") setNegotiations(asArray(n.value?.data));
    if (dis.status === "fulfilled") setDisputes(asArray(dis.value?.data));
    if (inv.status === "fulfilled") setInvoices(asArray(inv.value?.data?.invoices));

    if (prop.status === "fulfilled") {
      setRechercheProposals(asArray(prop.value?.data));
    } else {
      setRechercheProposals([]);
    }

    if (d.status === "fulfilled") {
      setMyAnnonces(asArray(d.value?.data));
    } else {
      const e = d.reason;
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Erreur de chargement des annonces";
      setAnnoncesLoadError(String(msg));
      toast.error(String(msg));
    }
    setAnnoncesLoaded(true);
  }, []);

  /** Après achat : onglet Mes achats + URL (ne dépend pas de markTabSeen pour pouvoir être défini tôt). */
  const goToMesAchats = useCallback(() => {
    setActiveTab("buys");
    try {
      window.localStorage.setItem(DASHBOARD_ACTIVE_TAB_KEY, "buys");
      const sp = new URLSearchParams(window.location.search || "");
      sp.set("tab", "buys");
      navigate({ pathname: "/dashboard", search: `?${sp.toString()}` }, { replace: true });
    } catch {
      navigate("/dashboard?tab=buys", { replace: true });
    }
  }, [navigate]);

  /** Après acceptation d’une suggestion : onglet Négociations + URL. */
  const goToNegotiations = useCallback(() => {
    setActiveTab("negotiations");
    try {
      window.localStorage.setItem(DASHBOARD_ACTIVE_TAB_KEY, "negotiations");
      const sp = new URLSearchParams(window.location.search || "");
      sp.set("tab", "negotiations");
      navigate({ pathname: "/dashboard", search: `?${sp.toString()}` }, { replace: true });
    } catch {
      navigate("/dashboard?tab=negotiations", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (location.pathname !== "/dashboard") return;
    void load();
  }, [load, location.pathname, location.key]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (location.pathname !== "/dashboard") return;
      void load();
    };
    document.addEventListener("visibilitychange", refreshIfVisible);
    window.addEventListener("focus", refreshIfVisible);
    return () => {
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.removeEventListener("focus", refreshIfVisible);
    };
  }, [load, location.pathname]);

  const runDashboardPendingBuyLoop = useCallback(async (p) => {
    if (!p?.dossierId) return;
    for (let i = 0; i < 5; i += 1) {
      try {
        const buyParams = p.negId ? { negotiation_id: p.negId } : {};
        const { data, initialPaymentMethods } = await postDossierBuyParallel(p.dossierId, buyParams);
        const outcome = await handleAcheterResponse(data, {
          toast,
          initialPaymentMethods,
          onOpenPaymentDialog: ({ clientSecret, transactionId, initialPaymentMethods: pm }) => {
            setPaymentDialog({
              open: true,
              clientSecret,
              txId: transactionId,
              dossierId: String(p.dossierId),
              negId: String(p.negId || ""),
              initialPaymentMethods: pm ?? initialPaymentMethods,
            });
          },
        });
        if (outcome === "done") {
          setPendingBuy(null);
          await load();
          goToMesAchats();
          return;
        }
        if (outcome === "modal") {
          setPendingBuy(null);
          return;
        }
        throw new Error("Paiement indisponible");
      } catch (e) {
        if (isSubscriptionRequiredError(e)) {
          await new Promise((r) => window.setTimeout(r, 1200));
          continue;
        }
        toast.error(e?.response?.data?.detail || e?.message || "Erreur paiement");
        setPendingBuy(null);
        return;
      }
    }
    toast.info("Abonnement en cours d’activation… réessaie dans quelques secondes.");
  }, [load, goToMesAchats]);

  const handleSubscriptionSyncRetry = useCallback(async () => {
    const { plan, paymentIntentId, pendingBuySnapshot } = subscriptionSyncDialog;
    if (!paymentIntentId) return;
    setSubscriptionSyncDialog((prev) => ({ ...prev, syncing: true }));
    try {
      await recoverSubscription(api, { plan, paymentIntentId });
      const ok = await waitForActiveSubscription(api);
      if (ok) {
        setSubscriptionSyncDialog({
          open: false,
          plan: "monthly",
          paymentIntentId: "",
          syncing: false,
          pendingBuySnapshot: null,
        });
        toast.success("Abonnement actif");
        await runDashboardPendingBuyLoop(pendingBuySnapshot);
      } else {
        toast.error("Encore en attente — réessayez dans quelques secondes.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Synchronisation impossible");
    } finally {
      setSubscriptionSyncDialog((prev) => ({ ...prev, syncing: false }));
    }
  }, [subscriptionSyncDialog, runDashboardPendingBuyLoop]);

  const buyerCancelNoContact = useCallback(
    async (t) => {
      const ok = window.confirm(
        "Annuler sans contact client ? Uniquement si aucun appel n’a été lancé pour cet achat. Sinon, terminez l’appel (RDV ou refus dossier).",
      );
      if (!ok) return;
      setBuyerCancelBusyId(t.transaction_id);
      try {
        await api.post("/call/buyer-cancel-contact", { transaction_id: t.transaction_id });
        toast.success("Annulation enregistrée — remboursement si un paiement était réservé.");
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || e?.message || "Impossible d’annuler");
      } finally {
        setBuyerCancelBusyId(null);
      }
    },
    [load],
  );

  const closeInlineCall = useCallback(async () => {
    const tid = inlineCallTx?.transaction_id;
    if (tid) {
      try {
        await api.post("/call/abort", { transaction_id: tid });
      } catch {
        /* filet webhook */
      }
    }
    setInlineCallTx(null);
    setInlineCallOpenOutcome(false);
    setInlineCallForceRetry(false);
    void load();
  }, [inlineCallTx, load]);

  /** Ferme le détail achat : sinon la modale Radix (modal) bloque clics/saisie sur l’appel par-dessus. */
  const openInlineCall = useCallback((t, { openOutcome = false, forceRetry = false } = {}) => {
    setDashBuyDetailTx(null);
    setDashBuyPane("main");
    setInlineCallOpenOutcome(openOutcome);
    setInlineCallForceRetry(forceRetry);
    setInlineCallTx(t);
  }, []);

  const getBuyActionFlags = useCallback(
    (t) => {
      if (!t) return null;
      const txKey = normTxId(t.transaction_id ?? t.id);
      const litigesForT =
        Array.isArray(t.litiges) && t.litiges.length
          ? t.litiges
          : disputes.filter((d) => normTxId(d?.transaction_id) === txKey);
      const canCall = t.statut === "paye" || t.statut === "appel_en_cours";
      const canBuyerCancelNoContact = ["en_attente", "paye", "appel_en_cours"].includes(t.statut);
      const callLineBusy = Boolean(callLockByTx[String(t.transaction_id ?? t.id)]);
      const canConfirm = t.statut === "appel_effectue";
      const alreadyRated = Boolean(txKey && myRatedTxIds.has(txKey));
      const canRateEligible = t.statut === "fonds_debloques" || t.statut === "rembourse";
      const canRate = canRateEligible && !alreadyRated;
      const disputeWindowOpen = isBuyerDisputeWindowOpen(t);
      const platformBlocksDispute = isDisputeOpeningBlockedByPlatform(t);
      const canOpenDispute =
        disputeWindowOpen && !platformBlocksDispute && litigesForT.length === 0;
      const closedLit = latestClosedDisputeByTx.get(txKey);
      const loserVerdictNotice =
        user?.user_id && closedLit ? disputeLoserNotice(user.user_id, t, closedLit) : null;
      return {
        txKey,
        litigesForT,
        canCall,
        canBuyerCancelNoContact,
        callLineBusy,
        canConfirm,
        canRate,
        canOpenDispute,
        loserVerdictNotice,
        isUnlocked: buyerPrivateUnlocked(t, litigesForT),
      };
    },
    [disputes, user?.user_id, myRatedTxIds, callLockByTx, latestClosedDisputeByTx],
  );

  const renderBuyQuickActions = useCallback(
    (t) => {
      const f = getBuyActionFlags(t);
      if (!f) return null;
      const busyCancel = buyerCancelBusyId === t.transaction_id;
      const hasAny =
        f.canCall ||
        f.canConfirm ||
        f.canBuyerCancelNoContact ||
        f.canOpenDispute ||
        f.canRate ||
        f.loserVerdictNotice;
      if (!hasAny) {
        return <span className="px-1 text-[10px] text-slate-400">—</span>;
      }
      return (
        <>
          {f.canCall && f.callLineBusy ? (
            <span className="max-w-[7.5rem] text-center text-[10px] font-medium leading-tight text-amber-800">
              Client occupé
            </span>
          ) : null}
          {f.canCall && !f.callLineBusy ? (
            <Button
              type="button"
              size="sm"
              className="btn-call-action h-8 rounded-lg px-2.5 text-xs"
              data-testid={`buy-row-call-${t.transaction_id}`}
              onClick={() => openInlineCall(t)}
            >
              <Phone className="mr-1 h-3.5 w-3.5 shrink-0" aria-hidden />
              Appeler
            </Button>
          ) : null}
          {f.canConfirm ? (
            <Button
              type="button"
              size="sm"
              className="btn-primary h-8 rounded-lg px-2.5 text-xs"
              data-testid={`buy-row-confirm-${t.transaction_id}`}
              onClick={() => openInlineCall(t, { openOutcome: true })}
            >
              Décision RDV
            </Button>
          ) : null}
          {f.canConfirm ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 rounded-lg border-slate-300 px-2.5 text-xs"
              data-testid={`buy-row-retry-${t.transaction_id}`}
              onClick={() => openInlineCall(t, { forceRetry: true })}
            >
              Relancer
            </Button>
          ) : null}
          {f.canBuyerCancelNoContact ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 rounded-lg border-slate-300 px-2.5 text-xs text-slate-700"
              disabled={busyCancel}
              data-testid={`buy-row-cancel-${t.transaction_id}`}
              onClick={() => void buyerCancelNoContact(t)}
            >
              {busyCancel ? "…" : "Annuler"}
            </Button>
          ) : null}
          {f.canOpenDispute ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 w-8 rounded-lg p-0"
              title="Ouvrir un litige"
              data-testid={`buy-row-dispute-${t.transaction_id}`}
              onClick={() => setShowDispute(t)}
            >
              <Scale className="h-3.5 w-3.5" aria-hidden />
            </Button>
          ) : null}
          {f.canRate ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 rounded-lg px-2.5 text-xs"
              data-testid={`buy-row-rate-${t.transaction_id}`}
              onClick={() => setShowRating(t)}
            >
              <Star className="mr-1 h-3.5 w-3.5 shrink-0" aria-hidden />
              Noter
            </Button>
          ) : null}
          {f.loserVerdictNotice ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 w-8 rounded-lg border-amber-200 p-0 text-amber-950"
              title="Décision du litige"
              data-testid={`buy-row-verdict-${t.transaction_id}`}
              onClick={() => setVerdictNoticeDialog(f.loserVerdictNotice)}
            >
              <Gavel className="h-3.5 w-3.5" aria-hidden />
            </Button>
          ) : null}
        </>
      );
    },
    [getBuyActionFlags, buyerCancelBusyId, buyerCancelNoContact, openInlineCall],
  );

  useEffect(() => {
    // Reprise automatique après Stripe Checkout abonnement (success_url)
    try {
      const sp = new URLSearchParams(window.location.search || "");
      const dossierId = sp.get("resume_dossier_id");
      const negId = sp.get("resume_negotiation_id") || "";
      const fromCheckout = sp.get("checkout_success") === "1" || Boolean(sp.get("session_id"));
      if (!dossierId) return;
      if (!fromCheckout && !negId) return;

      sp.delete("resume_dossier_id");
      sp.delete("resume_negotiation_id");
      sp.delete("resume_ts");
      sp.delete("checkout_success");
      sp.delete("session_id");
      sp.delete("checkout_cancel");
      const next = `${window.location.pathname}${sp.toString() ? `?${sp}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", next);

      setPayingNegId(negId || null);
      (async () => {
        if (fromCheckout) {
          const active = await waitForActiveSubscription(api);
          if (!active) {
            toast.info("Finalisation de l’abonnement… réessayez dans quelques secondes.");
            setPayingNegId(null);
            return;
          }
          toast.success("Abonnement actif");
        }
        try {
          const buyParams = negId ? { negotiation_id: negId } : {};
          const { data, initialPaymentMethods } = await postDossierBuyParallel(dossierId, buyParams);
          const outcome = await handleAcheterResponse(data, {
            toast,
            initialPaymentMethods,
            onOpenPaymentDialog: ({ clientSecret, transactionId, initialPaymentMethods: pm }) => {
              setPaymentDialog({
                open: true,
                clientSecret,
                txId: transactionId,
                dossierId: String(dossierId),
                negId: String(negId),
                initialPaymentMethods: pm ?? initialPaymentMethods,
              });
            },
          });
          if (outcome === "done") {
            await load();
            goToMesAchats();
          }
        } catch (e) {
          if (isSubscriptionRequiredError(e)) {
            setSubscribeDialog({ open: true, plan: "monthly", dossierId: String(dossierId), negId: String(negId) });
          } else {
            toast.error(e.response?.data?.detail || e?.message || "Erreur paiement");
          }
        } finally {
          setPayingNegId(null);
        }
      })();
    } catch {
      // ignore
    }
  }, [load, goToMesAchats]);

  // Si l’URL change (ex: refresh / lien partagé) et contient un ?tab= valide, on se cale dessus.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const t = sp.get("tab");
      if (!t) return;
      if (t === DASH_OVERVIEW_TAB) {
        if (activeTab !== DASH_OVERVIEW_TAB) setActiveTab(DASH_OVERVIEW_TAB);
        return;
      }
      if (DASHBOARD_WORK_TABS.includes(t) && t !== activeTab) setActiveTab(t);
    } catch {
      // ignore
    }
  }, [location.search, activeTab]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search || "");
    const raw = sp.get("openDisputeTx");
    if (!raw) {
      consumedOpenDisputeParam.current = null;
      return;
    }
    if (consumedOpenDisputeParam.current === raw) return;
    const t = buys.find((b) => normTxId(b.transaction_id ?? b.id) === normTxId(raw));
    if (!t) return;
    if (isDisputeOpeningBlockedByPlatform(t)) {
      consumedOpenDisputeParam.current = raw;
      toast.error(OPEN_DISPUTE_BLOCKED_PLATFORM_DETAIL_FR);
      try {
        const nextSp = new URLSearchParams(location.search || "");
        nextSp.delete("openDisputeTx");
        const q = nextSp.toString();
        navigate({ pathname: location.pathname, search: q ? `?${q}` : "" }, { replace: true });
      } catch {
        /* ignore */
      }
      return;
    }
    consumedOpenDisputeParam.current = raw;
    let pre = "";
    try {
      pre = sessionStorage.getItem(`rdm_dispute_prefill_${raw}`) || "";
      sessionStorage.removeItem(`rdm_dispute_prefill_${raw}`);
    } catch {
      /* ignore */
    }
    setShowDispute(t);
    setDisputeReason("autre");
    setDisputeDesc(
      pre ||
        "Je conteste la décision automatique après refus de RDV et demande l’examen d’un administrateur.",
    );
    try {
      const nextSp = new URLSearchParams(location.search || "");
      nextSp.delete("openDisputeTx");
      const q = nextSp.toString();
      navigate({ pathname: location.pathname, search: q ? `?${q}` : "" }, { replace: true });
    } catch {
      /* ignore */
    }
  }, [location.search, buys, navigate, location.pathname]);

  useEffect(() => {
    if (activeTab !== "negotiations") return undefined;
    void refreshNegotiations();
    const id = window.setInterval(() => {
      void refreshNegotiations();
    }, 4000);
    return () => window.clearInterval(id);
  }, [activeTab, refreshNegotiations]);

  const refreshRechercheProposals = useCallback(async () => {
    try {
      const res = await api.get("/recherche-proposals/mine", { params: { role: "buyer" } });
      setRechercheProposals(asArray(res?.data));
    } catch {
      setRechercheProposals([]);
    }
  }, []);

  const [proposalActionBusy, setProposalActionBusy] = useState(null);
  const respondRechercheProposal = useCallback(
    async (proposalId, action) => {
      setProposalActionBusy(proposalId);
      try {
        const res = await api.post("/recherche-proposals/respond", { proposal_id: proposalId, action });
        if (action === "accept") {
          toast.success("Proposition acceptée.");
          const p = (rechercheProposals || []).find((x) => String(x?.proposal_id) === String(proposalId));
          const venteId = p?.vente_dossier_id ? String(p.vente_dossier_id) : "";
          const rechercheId = p?.recherche_dossier_id ? String(p.recherche_dossier_id) : "";
          const negotiationId = res?.data?.negotiation_id ? String(res.data.negotiation_id) : "";
          setProposalChoice({
            open: true,
            proposalId: String(proposalId),
            rechercheId,
            venteId,
            negotiationId,
          });
        } else {
          toast.success("Proposition refusée.");
        }
        // Toujours rafraîchir les propositions visibles (statut pending/accepted/rejected)
        await refreshRechercheProposals();
        // Et le reste du dashboard (compteurs/états)
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || e?.message || "Erreur");
      } finally {
        setProposalActionBusy(null);
      }
    },
    [load, refreshRechercheProposals, rechercheProposals],
  );

  useEffect(() => {
    if (activeTab !== "suggestions") return undefined;
    void refreshRechercheProposals();
    const id = window.setInterval(() => {
      void refreshRechercheProposals();
    }, 8000);
    return () => window.clearInterval(id);
  }, [activeTab, refreshRechercheProposals]);

  const latestEpochFromRows = useCallback((rows, keys) => {
    const arr = Array.isArray(rows) ? rows : [];
    let maxTs = 0;
    for (const row of arr) {
      for (const key of keys) {
        const v = row?.[key];
        if (!v) continue;
        const ts = Date.parse(v);
        if (Number.isFinite(ts) && ts > maxTs) maxTs = ts;
      }
    }
    return maxTs;
  }, []);

  const pendingRechercheProposals = useMemo(
    () => rechercheProposals.filter((p) => p?.status === "pending"),
    [rechercheProposals],
  );

  const latestByTab = useMemo(
    () => ({
      buys: latestEpochFromRows(buys, ["created_at", "updated_at"]),
      sales: latestEpochFromRows(sales, ["created_at", "updated_at"]),
      annonces: latestEpochFromRows(myAnnonces, ["created_at", "updated_at"]),
      suggestions: latestEpochFromRows(pendingRechercheProposals, ["created_at"]),
      negotiations: latestEpochFromRows(negotiations, ["created_at", "updated_at"]),
      disputes: latestEpochFromRows(disputes, ["created_at", "updated_at"]),
      invoices: latestEpochFromRows(invoices, ["created_at", "date", "updated_at"]),
    }),
    [buys, sales, myAnnonces, pendingRechercheProposals, negotiations, disputes, invoices, latestEpochFromRows]
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(DASHBOARD_SEEN_TABS_KEY, JSON.stringify(seenByTab || {}));
    } catch {
      // ignore storage failure
    }
  }, [seenByTab]);

  useEffect(() => {
    setSeenByTab((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      for (const tab of Object.keys(latestByTab)) {
        if (!next[tab] && latestByTab[tab] > 0) {
          next[tab] = latestByTab[tab];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [latestByTab]);

  const dashboardTabFromUrl = useMemo(() => {
    try {
      const t = String(new URLSearchParams(location.search || "").get("tab") || "").trim();
      if (t === DASH_OVERVIEW_TAB) return DASH_OVERVIEW_TAB;
      if (DASHBOARD_WORK_TABS.includes(t)) return t;
    } catch {
      /* ignore */
    }
    return null;
  }, [location.search]);

  /** Onglet « marché » réellement affiché (URL prioritaire — sidebar = liens ?tab=). */
  const resolvedDashboardWorkTab = useMemo(() => {
    if (dashboardTabFromUrl && DASHBOARD_WORK_TABS.includes(dashboardTabFromUrl)) return dashboardTabFromUrl;
    if (DASHBOARD_WORK_TABS.includes(activeTab)) return activeTab;
    return null;
  }, [dashboardTabFromUrl, activeTab]);

  const hasNewByTab = useMemo(() => {
    const out = {};
    for (const tab of DASHBOARD_WORK_TABS) {
      if (tab === "invoices" || tab === "disputes") {
        out[tab] = false;
        continue;
      }
      const seen = Number(seenByTab?.[tab] || 0);
      const latest = Number(latestByTab?.[tab] || 0);
      const isCurrentTab = tab === resolvedDashboardWorkTab;
      out[tab] = !isCurrentTab && Boolean(seen > 0 && latest > seen);
    }
    return out;
  }, [latestByTab, seenByTab, resolvedDashboardWorkTab]);

  const markTabSeen = useCallback(
    (tab) => {
      if (!DASHBOARD_WORK_TABS.includes(tab)) return;
    setSeenByTab((prev) => {
      const latest = Number(latestByTab?.[tab] || 0);
      const nowTs = Date.now();
      const nextSeen = latest > 0 ? latest : nowTs;
      const current = Number(prev?.[tab] || 0);
      if (nextSeen <= current) return prev;
      return { ...(prev || {}), [tab]: nextSeen };
    });
    },
    [latestByTab]
  );

  /** Sidebar = liens URL : marquer « vu » à l’ouverture (clic ou ?tab=) + refresh données. */
  useEffect(() => {
    if (!resolvedDashboardWorkTab) return;
    markTabSeen(resolvedDashboardWorkTab);
  }, [resolvedDashboardWorkTab, latestByTab, markTabSeen]);

  const handleDashboardTabChange = useCallback(
    (v) => {
      setActiveTab(v);
      markTabSeen(v);
      try {
        const sp = new URLSearchParams(window.location.search || "");
        sp.delete("dossier");
        if (v === DASH_OVERVIEW_TAB) {
          window.localStorage.removeItem(DASHBOARD_ACTIVE_TAB_KEY);
          sp.set("tab", DASH_OVERVIEW_TAB);
          navigate({ pathname: "/dashboard", search: `?${sp.toString()}` }, { replace: true });
          return;
        }
        if (DASHBOARD_WORK_TABS.includes(v)) {
          window.localStorage.setItem(DASHBOARD_ACTIVE_TAB_KEY, v);
          sp.set("tab", v);
          navigate({ pathname: "/dashboard", search: `?${sp.toString()}` }, { replace: true });
        }
      } catch {
        /* ignore */
      }
    },
    [markTabSeen, navigate]
  );

  const memberDashboardNav = useMemo(() => buildMemberDashboardNav(hasNewByTab), [hasNewByTab]);

  const dashboardHeaderMeta = useMemo(() => {
    const M = {
      overview: {
        title: "Vue d'ensemble",
        subtitle: "Synthèse de votre activité et accès aux rubriques détaillées.",
      },
      buys: {
        title: "Mes achats",
        subtitle: "Paiements, appels et décisions après RDV.",
      },
      sales: {
        title: "Mes ventes",
        subtitle: "Pipeline vendeur et encaissements Stripe.",
      },
      annonces: {
        title: "Mes annonces",
        subtitle: "Vos fiches publiées et brouillons.",
      },
      suggestions: {
        title: "Suggestions",
        subtitle: "Propositions reçues sur vos annonces en recherche.",
      },
      negotiations: {
        title: "Négociations",
        subtitle: "Offres, contre-propositions et finalisation.",
      },
      disputes: {
        title: "Litiges",
        subtitle: "Suivi des dossiers ouverts et clos.",
      },
      invoices: {
        title: "Factures",
        subtitle: "Justificatifs et documents à télécharger.",
      },
    };
    return M[activeTab] || M.overview;
  }, [activeTab]);

  const userOverviewMetrics = useMemo(() => {
    const openDisputes = disputes.filter((d) => {
      const s = String(d?.status ?? d?.statut ?? "").toLowerCase();
      return s === "open" || s === "in_review";
    }).length;
    const achatsSuivis = buys.length;
    const achatsActifs = buys.filter((b) => {
      const st = String(b?.statut ?? "");
      return st !== "rembourse" && st !== "annule";
    }).length;
    const ventesTot = sales.length;
    const ventesEnCours = sales.filter((tx) => {
      const st = String(tx?.statut ?? "");
      return st !== "fonds_debloques" && st !== "rembourse" && st !== "annule";
    }).length;
    return {
      openDisputes,
      achatsSuivis,
      achatsActifs,
      ventesTot,
      ventesEnCours,
      annoncesCt: myAnnonces.length,
      suggestionsPending: pendingRechercheProposals.length,
      negCt: negotiations.length,
      invCt: invoices.length,
    };
  }, [
    buys,
    sales,
    myAnnonces,
    disputes,
    negotiations,
    invoices,
    pendingRechercheProposals.length,
  ]);

  const dashDisputeDetailRow = useMemo(() => {
    if (!dashDisputeDetailKey) return null;
    return (
      disputes.find((d) => {
        const disputeKey = d?.dispute_id ?? d?.id ?? `${d?.transaction_id || "litige"}_${d?.created_at || ""}`;
        return String(disputeKey) === dashDisputeDetailKey;
      }) ?? null
    );
  }, [dashDisputeDetailKey, disputes]);

  const dashInvoiceDetailRow = useMemo(() => {
    if (!dashInvoiceNumber) return null;
    return invoices.find((inv) => String(inv.invoice_number) === dashInvoiceNumber) ?? null;
  }, [dashInvoiceNumber, invoices]);

  const dashProposalDetailRow = useMemo(() => {
    if (!dashProposalId) return null;
    return rechercheProposals.find((pr) => String(pr.proposal_id) === dashProposalId) ?? null;
  }, [dashProposalId, rechercheProposals]);

  const dashNegDetailRow = useMemo(() => {
    if (!dashNegDetailId) return null;
    return negotiations.find((n) => String(n.negotiation_id) === dashNegDetailId) ?? null;
  }, [dashNegDetailId, negotiations]);

  const dashAnnonceDetailRow = useMemo(() => {
    if (!dashAnnonceDetailId) return null;
    return myAnnonces.find((d) => String(d.dossier_id) === dashAnnonceDetailId) ?? null;
  }, [dashAnnonceDetailId, myAnnonces]);

  const dashBuyDetailRow = useMemo(() => {
    if (!dashBuyDetailTx) return null;
    return displayBuys.find((t) => normTxId(t.transaction_id ?? t.id) === dashBuyDetailTx) ?? null;
  }, [dashBuyDetailTx, displayBuys]);

  useEffect(() => {
    if (!dashBuyDetailRow || !dashBuyDetailTx) {
      setBuyDetailDossier(null);
      setBuyDetailDossierLoading(false);
      return undefined;
    }
    const txKey = normTxId(dashBuyDetailRow.transaction_id ?? dashBuyDetailRow.id);
    const litigesForT = disputes.filter((d) => normTxId(d?.transaction_id) === txKey);
    if (!buyerPrivateUnlocked(dashBuyDetailRow, litigesForT) || !dashBuyDetailRow.dossier_id) {
      setBuyDetailDossier(null);
      setBuyDetailDossierLoading(false);
      return undefined;
    }
    let cancelled = false;
    setBuyDetailDossierLoading(true);
    api
      .get(`/dossiers/${dashBuyDetailRow.dossier_id}`)
      .then((res) => {
        if (!cancelled) setBuyDetailDossier(res?.data || null);
      })
      .catch(() => {
        if (!cancelled) setBuyDetailDossier(null);
      })
      .finally(() => {
        if (!cancelled) setBuyDetailDossierLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dashBuyDetailRow, dashBuyDetailTx, disputes]);

  const dashSaleDetailRow = useMemo(() => {
    if (!dashSaleDetailTx) return null;
    return filteredSales.find((t) => normTxId(t.transaction_id ?? t.id) === dashSaleDetailTx) ?? null;
  }, [dashSaleDetailTx, filteredSales]);

  const priorityActions = useMemo(() => {
    const items = [];
    const decisions = buys.filter((b) => b.statut === "appel_effectue").length;
    if (decisions > 0) {
      items.push({ tab: "buys", label: "Décision à prendre après votre appel", count: decisions });
    }
    const callsPending = buys.filter((b) => b.statut === "paye" || b.statut === "appel_en_cours").length;
    if (callsPending > 0) {
      items.push({ tab: "buys", label: "Achats : lancer ou suivre un appel", count: callsPending });
    }
    if (pendingRechercheProposals.length > 0) {
      items.push({
        tab: "suggestions",
        label: "Propositions à traiter sur vos annonces",
        count: pendingRechercheProposals.length,
      });
    }
    const negsNeedingResponse = negotiations.filter((n) => {
      if (String(n?.status || "") !== "open") return false;
      const lastOffer = n?.offers?.[n.offers.length - 1];
      return lastOffer && String(lastOffer.from_user_id || "") !== String(user?.user_id || "");
    }).length;
    if (negsNeedingResponse > 0) {
      items.push({
        tab: "negotiations",
        label: "Négociations : répondre à une offre",
        count: negsNeedingResponse,
      });
    }
    // Litige ouvert / en revue = attente équipe RdvMarket — jamais en priorité.
    return items.slice(0, 5);
  }, [buys, negotiations, pendingRechercheProposals.length, user?.user_id]);

  const priorityTabSet = useMemo(() => new Set(priorityActions.map((p) => p.tab)), [priorityActions]);

  const overviewKpiCards = useMemo(
    () =>
      [
        {
          tab: "buys",
          label: "Achats suivis",
          value: userOverviewMetrics.achatsSuivis,
          hint: `${userOverviewMetrics.achatsActifs} actif(s) / non terminé(s)`,
          Icon: MEMBER_DASH_TAB_ICONS.buys,
        },
        {
          tab: "sales",
          label: "Ventes",
          value: userOverviewMetrics.ventesTot,
          hint: `${userOverviewMetrics.ventesEnCours} en cours`,
          Icon: MEMBER_DASH_TAB_ICONS.sales,
        },
        {
          tab: "annonces",
          label: "Mes annonces",
          value: userOverviewMetrics.annoncesCt,
          hint: "Fiches créées ou publiées",
          Icon: MEMBER_DASH_TAB_ICONS.annonces,
        },
        {
          tab: "negotiations",
          label: "Négociations",
          value: userOverviewMetrics.negCt,
          hint: "En cours avec une contrepartie",
          Icon: MEMBER_DASH_TAB_ICONS.negotiations,
        },
        {
          tab: "suggestions",
          label: "Propositions en attente",
          value: userOverviewMetrics.suggestionsPending,
          hint: "Réponses à vos annonces « recherche »",
          Icon: MEMBER_DASH_TAB_ICONS.suggestions,
        },
        {
          tab: "invoices",
          label: "Factures",
          value: userOverviewMetrics.invCt,
          hint: "Documents téléchargeables",
          Icon: MEMBER_DASH_TAB_ICONS.invoices,
        },
      ],
    [userOverviewMetrics],
  );

  useEffect(() => {
    api.get("/produits")
      .then(({ data }) => setEditProduits(Array.isArray(data) ? data : []))
      .catch(() => setEditProduits([]));
  }, []);

  const editSecteurs = useMemo(
    () => [...new Set(editProduits.map((p) => p?.secteur).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")),
    [editProduits]
  );

  const editProduitsFiltres = useMemo(() => {
    if (!editAnnonceForm?.secteur) return [];
    return editProduits.filter((p) => p?.secteur === editAnnonceForm.secteur);
  }, [editProduits, editAnnonceForm?.secteur]);

  const editProduitSelectionne = useMemo(
    () => editProduits.find((p) => String(p?.produit_id) === String(editAnnonceForm?.produit_id || "")),
    [editProduits, editAnnonceForm?.produit_id]
  );

  const editDeptChampKey = useMemo(() => {
    const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
    const t = normalizeAnnonceType(editAnnonceForm?.type);
    const hit = champs.find((c) => {
      if (!c?.nom) return false;
      const ca = c.cible_annonces;
      const cibleOk = ca == null || ca === "tous" || ca === t;
      if (!cibleOk) return false;
      return isDepartementChampKey(c.nom);
    });
    return hit?.nom ? String(hit.nom) : null;
  }, [editProduitSelectionne, editAnnonceForm?.type]);

  const editChampsDynamiques = useMemo(() => {
    const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
    const t = normalizeAnnonceType(editAnnonceForm?.type);
    return champs.filter((c) => {
      if (!c?.nom) return false;
      if (editDeptChampKey && String(c.nom) === editDeptChampKey) return false;
      // Évite d'afficher en double les champs "contact/adresse" déjà gérés par les champs système.
      if (isContactLikeChamp(c.nom)) return false;
      if (t === "recherche" && c.visible_public === false) return false;
      const ca = c.cible_annonces;
      if (ca == null || ca === "tous") return true;
      return ca === t;
    });
  }, [editProduitSelectionne, editAnnonceForm?.type, editDeptChampKey]);

  const editChampsMasquesRecherche = useMemo(() => {
    if (normalizeAnnonceType(editAnnonceForm?.type) !== "recherche") return [];
    const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
    return champs
      .filter((c) => {
        if (!c?.nom) return false;
        if (c.visible_public !== false) return false;
        const ca = c.cible_annonces;
        return ca == null || ca === "tous" || ca === "recherche";
      })
      .map((c) => String(c.nom));
  }, [editAnnonceForm?.type, editProduitSelectionne]);

  /** Préremplissage département à l’ouverture uniquement — ne pas réécrire quand l’utilisateur efface. */
  useEffect(() => {
    if (!editingAnnonce?.dossier_id || !editDeptChampKey) return;
    if (normalizeAnnonceType(editAnnonceForm?.type) !== "recherche") return;

    setEditAnnonceForm((p) => {
      const cf = p.custom_fields && typeof p.custom_fields === "object" ? { ...p.custom_fields } : {};
      if (cf[editDeptChampKey] != null && String(cf[editDeptChampKey]).trim() !== "") return p;
      const { raw } = findDepartementFieldRaw(editingAnnonce);
      if (!raw) return p;
      return { ...p, custom_fields: { ...cf, [editDeptChampKey]: raw } };
    });
  }, [editingAnnonce, editDeptChampKey, editAnnonceForm?.type]);

  useEffect(() => {
    const relevants = buys.filter((t) => t.statut === "paye" || t.statut === "appel_en_cours");
    if (relevants.length === 0) return undefined;
    const cleanups = relevants.map((t) => {
      const dossierId = String(t.dossier_id || "");
      const txId = String(t.transaction_id ?? t.id ?? "");
      if (!dossierId || !txId) return () => {};
      return subscribeDossierCallLock(
        dossierId,
        (busy) => {
          setCallLockByTx((prev) => ({ ...prev, [txId]: busy }));
        },
        { excludeTransactionId: txId, uniqueSuffix: `buy-${txId}` },
    );
    });
    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [buys]);

  const downloadInvoice = async (kind, id) => {
    try {
      const res = await api.get(`/invoices/${kind}/${id}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `facture-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Facture indisponible");
    }
  };

  const submitRating = async () => {
    const txId = showRating?.transaction_id ?? showRating?.id;
    if (!txId) {
      toast.error("Transaction introuvable pour la notation.");
      return;
    }
    setSubmittingRating(true);
    try {
      const res = await api.post("/ratings", { transaction_id: txId, stars: ratingValue, comment: "" });
      const payload = res?.data ?? res;
      if (payload?.alreadyRated) {
        toast.info("Vous aviez déjà noté cette transaction.");
      } else {
        toast.success("Merci pour votre évaluation");
      }
      setShowRating(null);
      setRatingValue(0);
      setMyRatedTxIds((prev) => new Set(prev).add(normTxId(txId)));
      load();
    } catch (e) {
      const detail = String(e?.response?.data?.detail || e?.message || "").toLowerCase();
      if (
        detail.includes("unique") ||
        detail.includes("duplicate") ||
        detail.includes("idx_notations_transaction_author") ||
        detail.includes("déjà noté")
      ) {
        toast.info("Vous aviez déjà noté cette transaction.");
        setShowRating(null);
        setRatingValue(0);
        setMyRatedTxIds((prev) => new Set(prev).add(normTxId(txId)));
        void load();
      } else {
        toast.error(e.response?.data?.detail || "Erreur");
      }
    } finally {
      setSubmittingRating(false);
    }
  };

  const openDispute = async () => {
    const txId = showDispute?.transaction_id ?? showDispute?.id;
    if (!txId) {
      toast.error("Identifiant de transaction manquant.");
      return;
    }
    try {
      await api.post("/disputes", { transaction_id: txId, reason: disputeReason, description: disputeDesc });
      toast.success("Litige ouvert, un admin vous répondra sous 72h");
      setShowDispute(null); setDisputeReason(""); setDisputeDesc("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const respondNegotiation = async (negId, action, counter = null) => {
    try {
      const params = { action };
      if (counter) params.counter_amount = counter;
      await api.post(`/negotiations/${negId}/respond`, null, { params });
      toast.success("Réponse envoyée");
      refreshNegotiations();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const sendNegotiationOffer = async (neg) => {
    const negId = neg?.negotiation_id;
    if (!negId) return;
    const raw = negOfferAmount[negId];
    const amt = Number(String(raw ?? "").replace(",", "."));
    if (!amt || amt <= 0) return toast.error("Montant invalide");
    setSendingNegOfferId(negId);
    const optimisticOffer = {
      offer_id: `tmp_${Date.now()}`,
      from_user_id: user?.user_id,
      amount: amt,
      status: "sent",
      created_at: new Date().toISOString(),
    };
    setNegotiations((prev) =>
      (Array.isArray(prev) ? prev : []).map((n) => {
        if ((n?.negotiation_id ?? n?.id) !== negId) return n;
        const offers = Array.isArray(n?.offers) ? n.offers : [];
        return { ...n, offers: [...offers, optimisticOffer] };
      })
    );
    setNegOfferAmount((p) => ({ ...(p || {}), [negId]: "" }));
    try {
      await api.post(`/negotiations/${negId}/offers`, { amount: amt });
      toast.success("Offre envoyée");
      refreshNegotiations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      refreshNegotiations();
    } finally {
      setSendingNegOfferId(null);
    }
  };

  const confirmDeleteNegotiation = async () => {
    const negId = deleteNegDialog?.neg?.negotiation_id;
    if (!negId) return;
    setDeletingNegId(negId);
    try {
      await api.delete(`/negotiations/${negId}`);
      toast.success("Négociation supprimée");
      setDeleteNegDialog({ open: false, neg: null });
      refreshNegotiations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally {
      setDeletingNegId(null);
    }
  };

  const buyFromNegotiation = async (neg) => {
    const negId = neg?.negotiation_id;
    const dossier = neg?.dossiers || neg?.dossier || null;
    const dossierId = neg?.dossier_id ?? dossier?.id ?? null;
    if (!negId || !dossierId) return;
    setPayingNegId(negId);
    try {
      const { data, initialPaymentMethods } = await postDossierBuyParallel(dossierId, {
        negotiation_id: negId,
      });
      const outcome = await handleAcheterResponse(data, {
        toast,
        initialPaymentMethods,
        onOpenPaymentDialog: ({ clientSecret, transactionId, initialPaymentMethods: pm }) => {
          setPaymentDialog({
            open: true,
            clientSecret,
            txId: transactionId,
            dossierId: String(dossierId),
            negId: String(negId),
            initialPaymentMethods: pm ?? initialPaymentMethods,
          });
        },
      });
      if (outcome === "done") {
        await load();
        goToMesAchats();
      }
    } catch (e) {
      if (isSubscriptionRequiredError(e)) {
        const dossierIdStr = String(dossierId);
        const negIdStr = String(negId);
        setPendingBuy({ dossierId: dossierIdStr, negId: negIdStr });
        setSubscribeDialog({ open: true, plan: "monthly", dossierId: String(dossierId), negId: String(negId) });
      } else if (e.response?.status === 409) {
        toast.error(e.response?.data?.detail || "Commande déjà en cours pour cette annonce.");
      } else {
        toast.error(e.response?.data?.detail || "Erreur paiement");
      }
    } finally {
      setPayingNegId(null);
    }
  };

  const startSubscribe = async (plan) => {
    const dossierId = subscribeDialog?.dossierId || pendingBuy?.dossierId;
    const negId = subscribeDialog?.negId ?? pendingBuy?.negId ?? "";
    setSubscribing(true);
    try {
      if (dossierId) setPendingBuy({ dossierId: String(dossierId), negId: String(negId || "") });
      await startSubscriptionCheckout(api, {
        plan,
        resumeDossierId: dossierId,
        resumeNegId: negId,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Impossible d’ouvrir Stripe");
      setSubscribing(false);
    }
  };

  const shareAnnonce = async (d) => {
    const href = `/dossier/${d.dossier_id}`;
    const shareUrl = `${window.location.origin}${href}`;
    try {
      if (navigator.share) {
        await navigator.share({ url: shareUrl, title: d.title || "Annonce RdvMarket" });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Lien copié");
      } else {
        toast.info("Partage indisponible sur cet appareil");
      }
    } catch {
      // no-op (annulation utilisateur)
    }
  };

  const openEditAnnonce = (d) => {
    setEditingAnnonce(d);
    setEditAnnonceForm({
      type: normalizeAnnonceType(d?.type) || "vente",
      secteur: d?.secteur || "",
      produit_id: d?.produit_id || "",
      title: d?.title || "",
      description: d?.description || "",
      location: d?.location || "",
      client_name: d?.client_name || "",
      client_phone: d?.client_phone || "",
      price: d?.price != null ? String(d.price) : "",
      price_min: d?.price_min != null ? String(d.price_min) : "",
      price_max: d?.price_max != null ? String(d.price_max) : "",
      custom_fields: d?.custom_fields && typeof d.custom_fields === "object" ? { ...d.custom_fields } : {},
    });
  };

  const saveAnnonceEdits = async () => {
    if (!editingAnnonce?.dossier_id) return;
    const asNullableNumber = (v) => {
      if (v == null || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const stripRechercheContactFields = (customFieldsIn) => {
      const cf = customFieldsIn && typeof customFieldsIn === "object" ? { ...customFieldsIn } : {};
      const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
      for (const c of champs) {
        const nom = c?.nom;
        if (!nom) continue;
        if (c.visible_public === false || isClientIdentityChamp(nom)) {
          delete cf[nom];
        }
      }
      for (const k of Object.keys(cf)) {
        if (isClientIdentityChamp(k)) delete cf[k];
      }
      return cf;
    };
    const syncContactIntoCustomFields = (customFieldsIn) => {
      const cf = customFieldsIn && typeof customFieldsIn === "object" ? { ...customFieldsIn } : {};
      const champs = Array.isArray(editProduitSelectionne?.champs) ? editProduitSelectionne.champs : [];
      const setIfPresent = (nom, value) => {
        if (value == null || String(value).trim() === "") return;
        cf[nom] = value;
      };
      // Si le produit contient ces champs, on garde une valeur cohérente.
      for (const c of champs) {
        const nom = c?.nom;
        if (!nom) continue;
        const k = normalizeChampKey(nom);
        if (k === "nom du client" || k === "nom du client/prospect") setIfPresent(nom, editAnnonceForm.client_name);
        if (k === "telephone du client" || k === "telephone du client/prospect" || k === "tel du client") setIfPresent(nom, editAnnonceForm.client_phone);
        if (k === "localisation" || k === "location") setIfPresent(nom, editAnnonceForm.location);
      }
      return cf;
    };
    const annonceType = normalizeAnnonceType(editAnnonceForm.type) || "vente";
    const rawCustomFields =
      editAnnonceForm.custom_fields && typeof editAnnonceForm.custom_fields === "object"
        ? editAnnonceForm.custom_fields
        : {};
    const payload = {
      type: annonceType,
      secteur: editAnnonceForm.secteur?.trim() || null,
      produit_id: editAnnonceForm.produit_id || null,
      title: editAnnonceForm.title?.trim(),
      description: editAnnonceForm.description?.trim(),
      location: editAnnonceForm.location?.trim(),
      client_name: annonceType === "recherche" ? null : editAnnonceForm.client_name?.trim() || null,
      client_phone: annonceType === "recherche" ? null : editAnnonceForm.client_phone?.trim() || null,
      custom_fields:
        annonceType === "recherche"
          ? stripRechercheContactFields(rawCustomFields)
          : syncContactIntoCustomFields(rawCustomFields),
    };
    if (payload.type === "recherche") {
      payload.price_min = asNullableNumber(editAnnonceForm.price_min);
      payload.price_max = asNullableNumber(editAnnonceForm.price_max);
      payload.price = payload.price_max ?? null;
      payload.recherche_criteres_requis = Array.isArray(editingAnnonce?.recherche_criteres_requis)
        ? editingAnnonce.recherche_criteres_requis
        : [];
      if (editDeptChampKey) {
        const deptRaw = String(payload.custom_fields?.[editDeptChampKey] ?? "").trim();
        const deptDisplay = formatDepartmentDisplay(deptRaw) || deptRaw;
        if (deptDisplay) {
          payload.custom_fields = { ...payload.custom_fields, [editDeptChampKey]: deptDisplay };
          payload.location = deptDisplay;
        } else {
          delete payload.custom_fields[editDeptChampKey];
          payload.location = null;
        }
      } else {
        payload.location = null;
      }
    } else {
      payload.price = asNullableNumber(editAnnonceForm.price);
      payload.price_min = null;
      payload.price_max = null;
      payload.recherche_criteres_requis = [];
    }

    if (payload.client_phone) {
      const telErr = phoneNormalizationError(payload.client_phone);
      if (telErr) {
        toast.error(telErr);
        return;
      }
    }

    setSavingAnnonce(true);
    try {
      await api.put(`/dossiers/${editingAnnonce.dossier_id}`, payload);
      toast.success("Annonce modifiée");
      setEditingAnnonce(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Modification impossible");
    } finally {
      setSavingAnnonce(false);
    }
  };

  const deleteAnnonce = async (d) => {
    if (!d?.dossier_id) return;
    const ok = window.confirm(
      "Retirer cette annonce de « Mes annonces » ?\n\nSi une vente ou une négociation existe déjà, l’historique sera conservé (pas de suppression définitive en base).",
    );
    if (!ok) return;
    setDeletingAnnonceId(d.dossier_id);
    try {
      const res = await api.delete(`/dossiers/${d.dossier_id}`);
      const removed = res?.data?.removed;
      if (removed === "retired") {
        toast.success(
          res?.data?.detail ||
            "Annonce retirée de votre liste. L’historique de vente est conservé.",
        );
      } else {
      toast.success("Annonce supprimée");
      }
      setDashAnnonceDetailId(null);
      setDashAnnoncePane("main");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Suppression impossible");
    } finally {
      setDeletingAnnonceId(null);
    }
  };

  const renderBuyTransactionDetail = (t, { onOpenDossier } = {}) => {
    if (!t) return null;
                  const st = statusLabels[t.statut] || {};
                  const canCall = t.statut === "paye" || t.statut === "appel_en_cours";
                  const canBuyerCancelNoContact = ["en_attente", "paye", "appel_en_cours"].includes(t.statut);
                  const callLineBusy = Boolean(callLockByTx[String(t.transaction_id ?? t.id)]);
                  /** Uniquement tant que la décision post-appel n’est pas enregistrée — pas après `fonds_debloques` / remboursement. */
                  const canConfirm = t.statut === "appel_effectue";
                  const txKey = normTxId(t.transaction_id ?? t.id);
                  const alreadyRated = Boolean(txKey && myRatedTxIds.has(txKey));
                  const canRateEligible = t.statut === "fonds_debloques" || t.statut === "rembourse";
                  const canRate = canRateEligible && !alreadyRated;
                  const litigesForT = disputes.filter((d) => normTxId(d?.transaction_id) === txKey);
                  const isUnlocked = buyerPrivateUnlocked(t, litigesForT);
                  /** Litige : uniquement après enregistrement du résultat RDV (plus d’étape « Décision requise »). */
                  const disputeWindowOpen = isBuyerDisputeWindowOpen(t);
                  const platformBlocksDispute = isDisputeOpeningBlockedByPlatform(t);
                  const canOpenDispute =
                    disputeWindowOpen && !platformBlocksDispute && litigesForT.length === 0;
                  const canShowDisputeHint =
                    (t.statut === "fonds_debloques" || t.statut === "rembourse") &&
                    disputeWindowOpen &&
                    (platformBlocksDispute || litigesForT.length === 0);
                  const closedLit = latestClosedDisputeByTx.get(txKey);
                  const loserVerdictNotice =
                    user?.user_id && closedLit ? disputeLoserNotice(user.user_id, t, closedLit) : null;
                  const winnerLabel =
                    user?.user_id && closedLit ? disputeWinnerLabel(user.user_id, t, closedLit) : null;
                  const dept = extractDeptFromText(t.dossier_location) || extractDeptFromText(t.departement) || extractDeptFromText(t.department);
    const locationMeta = isUnlocked
      ? t.dossier_location || "—"
      : dept
        ? `Dépt ${dept}`
        : "Département masqué";
    const contactRows = [];
    if (buyDetailDossier?.client_name) contactRows.push({ label: "Contact", value: buyDetailDossier.client_name });
    if (buyDetailDossier?.client_phone) {
      contactRows.push({
        label: "Téléphone",
        value: formatPhoneForDisplay(buyDetailDossier.client_phone),
        href: `tel:${String(buyDetailDossier.client_phone).replace(/\s/g, "")}`,
      });
    }
    if (buyDetailDossier?.location) contactRows.push({ label: "Adresse", value: buyDetailDossier.location });
    if (buyDetailDossier?.seller_name) contactRows.push({ label: "Vendeur", value: buyDetailDossier.seller_name });
    const openFiche = () => {
      if (!t.dossier_id) return;
      onOpenDossier?.();
    };
                  return (
      <div className="flex flex-col gap-5" data-testid={`buy-detail-${t.transaction_id ?? t.id}`}>
        <ModalSummaryBar
          badge={
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={cn("w-fit shrink-0", st.className)}>{st.label}</Badge>
              {winnerLabel ? (
                <span className="inline-flex items-center rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-900">
                  {winnerLabel}
                </span>
              ) : null}
            </div>
          }
          amount={`${Number(t.buyer_total).toLocaleString("fr-FR")}€`}
          meta={`${locationMeta} · ${new Date(t.created_at).toLocaleDateString("fr-FR")}`}
        />
        <TransactionTrackingCard tx={t} litiges={litigesForT} role="buyer" compact />
        {t.dossier_id ? (
          <div className="space-y-3">
                          {isUnlocked ? (
              buyDetailDossierLoading ? (
                <p className="text-sm text-slate-600">Chargement des coordonnées…</p>
              ) : (
                <ModalContactCard rows={contactRows} onMoreDetails={openFiche} moreLabel="Fiche annonce complète" />
              )
                            ) : (
                              <>
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                  Coordonnées et champs confidentiels masqués tant que le litige est en cours ou la vente n’est pas conclue.
                </p>
                <button
                  type="button"
                  className="text-sm font-semibold text-[#2D7FF9] hover:underline text-left"
                  onClick={openFiche}
                  data-testid={`buy-open-dossier-${t.transaction_id ?? t.id}`}
                >
                  Voir la fiche annonce →
                </button>
                              </>
                            )}
                      </div>
                        ) : null}
        {canShowDisputeHint ? (
          <p className="text-xs text-slate-600">
            {platformBlocksDispute
              ? "Ouverture de litige impossible (constat plateforme)."
              : `Litige possible sous ${SELLER_PAYOUT_HOURS} h après clôture de l’appel.`}
          </p>
        ) : null}
        <ModalActions>
                        {canCall && callLineBusy && (
                          <p className="text-sm text-amber-800 max-w-[280px]">
                            ⏳ Ce client est en ligne avec un concurrent. Réessayez dans quelques minutes.
                          </p>
                        )}
                        {canCall && !callLineBusy && (
                          <>
                            <Button
                              type="button"
                              className="btn-call-action rounded-lg"
                              data-testid={`call-menu-trigger-${t.transaction_id}`}
                              onClick={() => {
                                openInlineCall(t);
                                void load();
                              }}
                            >
                              <Phone className="w-4 h-4 mr-1 shrink-0" />
                              Lancer l’appel
                            </Button>
                            {canBuyerCancelNoContact ? (
                              <Button
                                variant="outline"
                                className="text-slate-600 border-slate-300"
                                disabled={buyerCancelBusyId === t.transaction_id}
                                onClick={() => void buyerCancelNoContact(t)}
                                data-testid={`buyer-cancel-contact-${t.transaction_id}`}
                              >
                                {buyerCancelBusyId === t.transaction_id ? "Annulation…" : "Annuler sans contact"}
                              </Button>
                            ) : null}
                          </>
                        )}
                        {canConfirm && (
                          <>
                            <Button
                              type="button"
                              className="btn-primary rounded-lg"
                              data-testid={`confirm-call-btn-${t.transaction_id}`}
                              onClick={() => openInlineCall(t, { openOutcome: true })}
                            >
                              Confirmer / refuser le RDV
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="text-slate-700 border-slate-300"
                              data-testid={`retry-call-btn-${t.transaction_id}`}
                              onClick={() => openInlineCall(t, { forceRetry: true })}
                            >
                              Relancer l’appel
                            </Button>
                          </>
                        )}
                        {canBuyerCancelNoContact && callLineBusy ? (
                          <Button
                            variant="outline"
                            className="text-slate-600 border-slate-300"
                            disabled={buyerCancelBusyId === t.transaction_id}
                            onClick={() => void buyerCancelNoContact(t)}
                            data-testid={`buyer-cancel-contact-busy-${t.transaction_id}`}
                          >
                            {buyerCancelBusyId === t.transaction_id ? "Annulation…" : "Annuler sans contact"}
                          </Button>
                        ) : null}
                        {canBuyerCancelNoContact && !canCall ? (
                          <Button
                            variant="outline"
                            className="text-slate-600 border-slate-300"
                            disabled={buyerCancelBusyId === t.transaction_id}
                            onClick={() => void buyerCancelNoContact(t)}
                            data-testid={`buyer-cancel-contact-pending-${t.transaction_id}`}
                          >
                            {buyerCancelBusyId === t.transaction_id ? "Annulation…" : "Annuler sans contact"}
                          </Button>
                        ) : null}
                        {canRateEligible && alreadyRated ? (
                          <span
                            className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600"
                            data-testid={`rated-done-${txKey}`}
                          >
                            Déjà noté
                          </span>
                        ) : canRate ? (
                          <Button
                            variant="outline"
                            onClick={() => setShowRating(t)}
                            data-testid={`rate-btn-${t.transaction_id ?? t.id}`}
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Noter le vendeur
                          </Button>
                        ) : null}
                        {canOpenDispute && (
                          <Button
                            variant="outline"
                            onClick={() => setShowDispute(t)}
                            data-testid={`dispute-btn-${t.transaction_id}`}
                            title="Litige"
                          >
                            <Scale className="w-4 h-4" />
                          </Button>
                        )}
                        {loserVerdictNotice ? (
                          <Button
                            variant="outline"
                            className="border-amber-200 text-amber-950 hover:bg-amber-50"
                            onClick={() => setVerdictNoticeDialog(loserVerdictNotice)}
                            data-testid={`dispute-verdict-btn-${t.transaction_id ?? t.id}`}
                            title="Décision du litige"
                          >
                            <Gavel className="w-4 h-4 mr-1 shrink-0" />
                            Décision litige
                          </Button>
                        ) : null}
        </ModalActions>
                      </div>
    );
  };

  const renderSaleTransactionDetail = (t, { onOpenDossier } = {}) => {
    if (!t) return null;
    const st = sellerSaleStatusBadge(t, { payoutsReady });
    const payoutHint = sellerPayoutScheduleHint(t);
    const saleTxKey = normTxId(t.transaction_id ?? t.id);
    const saleClosedLit = latestClosedDisputeByTx.get(saleTxKey);
    const saleLoserNotice =
      user?.user_id && saleClosedLit ? disputeLoserNotice(user.user_id, t, saleClosedLit) : null;
    const saleWinnerLabel =
      user?.user_id && saleClosedLit ? disputeWinnerLabel(user.user_id, t, saleClosedLit) : null;
    const alreadyRatedSale = Boolean(saleTxKey && myRatedTxIds.has(saleTxKey));
    const canRateSaleEligible = t.statut === "fonds_debloques" || t.statut === "rembourse";
    const canRateSale = canRateSaleEligible && !alreadyRatedSale;
    return (
      <div className="flex flex-col gap-5" data-testid={`sale-detail-${t.transaction_id ?? t.id}`}>
        <ModalSummaryBar
          badge={
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={cn("w-fit shrink-0", st.className)}>{st.label}</Badge>
              {saleWinnerLabel ? (
                <span className="inline-flex items-center rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-900">
                  {saleWinnerLabel}
                </span>
              ) : null}
            </div>
          }
          amount={`+${Number(t.seller_net).toLocaleString("fr-FR")}€`}
          meta={`${new Date(t.created_at).toLocaleDateString("fr-FR")}${payoutHint ? ` · ${payoutHint}` : ""}`}
        />
        <TransactionTrackingCard
          tx={t}
          litiges={disputes.filter((d) => normTxId(d?.transaction_id) === saleTxKey)}
          role="seller"
          compact
        />
        {t.dossier_id ? (
          <button
            type="button"
            className="text-sm font-semibold text-[#2D7FF9] hover:underline text-left"
            onClick={() => onOpenDossier?.()}
          >
            Voir la fiche annonce →
          </button>
        ) : null}
        <ModalActions>
          {saleLoserNotice ? (
            <Button
              variant="outline"
              size="sm"
              className="border-amber-200 text-amber-950 hover:bg-amber-50"
              onClick={() => setVerdictNoticeDialog(saleLoserNotice)}
              data-testid={`sale-dispute-verdict-btn-${t.transaction_id ?? t.id}`}
            >
              <Gavel className="w-4 h-4 mr-1 shrink-0" />
              Décision litige
            </Button>
          ) : null}
          {canRateSaleEligible && alreadyRatedSale ? (
            <span
              className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600"
              data-testid={`sale-rated-done-${saleTxKey}`}
            >
              Déjà noté
            </span>
          ) : canRateSale ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRating(t)}
              data-testid={`sale-rate-btn-${t.transaction_id ?? t.id}`}
            >
              <Star className="w-4 h-4 mr-1" />
              Noter l’acheteur
            </Button>
          ) : null}
        </ModalActions>
                    </div>
                  );
  };

  const renderNegotiationDetail = (n, { onOpenDossier } = {}) => {
    if (!n) return null;
    const dossier = n?.dossiers || n?.dossier || null;
    const dossierId = n?.dossier_id ?? dossier?.id ?? null;
    const dossierTitle = n?.dossier_title ?? dossier?.title ?? dossier?.titre ?? "Annonce";
    const dossierPrice = n?.dossier_price ?? dossier?.price ?? null;
    const negotiatedPrice = n?.final_price ?? null;
    const geo = getGeoLabel(dossier);
    const lastOffer = n.offers?.[n.offers.length - 1];
    const needsResponse = lastOffer && lastOffer.from_user_id !== user?.user_id && n.status === "open";
    const isOpen = (n.status || "open") === "open";
    const isBuyer = (n?.buyer_id || n?.buyer || "") && String(n?.buyer_id || n?.buyer) === String(user?.user_id || "");
    const isRecherche = (dossier?.type || "") === "recherche";
    const alreadyBoughtThisDossier = hasBlockingPurchaseForDossier(buys, dossierId);
    const canPay = n?.status === "accepted" && isBuyer && !isRecherche && !alreadyBoughtThisDossier;
    const payingThisNeg = Boolean(paymentDialog?.open) && String(paymentDialog?.negId || "") === String(n?.negotiation_id || "");
    return (
      <div className="space-y-3" data-testid={`neg-${n.negotiation_id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <button
              type="button"
              className="font-display font-bold hover:text-[#2D7FF9] block truncate text-left"
              title={dossierTitle}
              disabled={!dossierId}
              onClick={() => dossierId && onOpenDossier?.()}
            >
              {dossierTitle}
            </button>
            <div className="mt-0.5 text-[11px] text-slate-500">
              Prix :{" "}
              <span className="font-semibold text-slate-700">
                {dossierPrice != null && dossierPrice !== "" ? `${Number(dossierPrice).toLocaleString("fr-FR")}€` : "—"}
              </span>{" "}
              {negotiatedPrice != null && negotiatedPrice !== "" && Number(negotiatedPrice) !== Number(dossierPrice) ? (
                <span className="ml-1.5">
                  · Négocié :{" "}
                  <span className="font-semibold text-slate-700">
                    {`${Number(negotiatedPrice).toLocaleString("fr-FR")}€`}
                  </span>
                </span>
              ) : null}
              {geo?.value ? ` · ${geo.value}` : ""} · {n.offers?.length || 0} offre(s)
              </div>
          </div>
          <div className="flex items-center gap-2">
            {n.status === "open" ? null : (
              <Badge className={n.status === "accepted" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200"}>
                {negotiationStatusLabelFr(n.status)}
              </Badge>
            )}
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-50 hover:text-slate-700"
              title="Supprimer cette négociation"
              aria-label="Supprimer cette négociation"
              onClick={() => setDeleteNegDialog({ open: true, neg: n })}
              data-testid={`neg-delete-${n.negotiation_id}`}
            >
              <X className="h-4 w-4" />
            </button>
                  </div>
                  </div>

        <div className="mt-3 flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[11px] text-slate-600 whitespace-nowrap">Dernière offre :</span>
            {lastOffer ? (
              <span className="font-display font-bold text-[#2D7FF9] tabular-nums whitespace-nowrap">
                {Number(lastOffer.amount).toLocaleString("fr-FR")}€
              </span>
            ) : (
              <span className="text-[11px] text-slate-500 whitespace-nowrap">—</span>
            )}
                  </div>

          {isOpen ? (
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <Input
                type="text"
                inputMode="decimal"
                placeholder="Montant (€)"
                className="h-9 w-full sm:w-40 font-mono tabular-nums"
                value={negOfferAmount[n.negotiation_id] ?? ""}
                onChange={(e) => setNegOfferAmount((p) => ({ ...(p || {}), [n.negotiation_id]: e.target.value }))}
                data-testid={`neg-amount-${n.negotiation_id}`}
              />
              <Button
                size="sm"
                className="btn-primary h-9"
                onClick={() => sendNegotiationOffer(n)}
                disabled={sendingNegOfferId === n.negotiation_id}
                data-testid={`neg-send-${n.negotiation_id}`}
              >
                {sendingNegOfferId === n.negotiation_id ? "Envoi..." : "Proposer"}
              </Button>
                  </div>
          ) : null}
                  </div>

        {n.status !== "open" ? null : null}

        {needsResponse && (
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              className="btn-call-action"
              onClick={() => respondNegotiation(n.negotiation_id, "accept")}
              data-testid={`neg-accept-${n.negotiation_id}`}
            >
              Accepter
                    </Button>
                    <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => respondNegotiation(n.negotiation_id, "reject")}
              data-testid={`neg-reject-${n.negotiation_id}`}
            >
              Refuser
                    </Button>
                </div>
                  )}

        {canPay && (
          <div className="mt-3">
            <Button
              className="btn-primary w-full"
              onClick={() => buyFromNegotiation(n)}
              disabled={payingNegId === n.negotiation_id || payingThisNeg}
              data-testid={`neg-pay-${n.negotiation_id}`}
            >
              {payingNegId === n.negotiation_id || payingThisNeg ? "Paiement..." : "Valider & payer"}
                    </Button>
                </div>
        )}
        {n?.status === "accepted" && isBuyer && !isRecherche && alreadyBoughtThisDossier ? (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Vous avez déjà une commande pour cette annonce (voir l’onglet{" "}
            <strong>Mes achats</strong>). Une double commande n’est pas possible.
          </p>
        ) : null}
              </div>
    );
  };

  return (
    <>
      <MemberShell
        title={dashboardHeaderMeta.title}
        subtitle={dashboardHeaderMeta.subtitle}
        dashboardNav={memberDashboardNav}
        onDashboardNavClick={markTabSeen}
      >
        <div className="mx-auto max-w-7xl space-y-6">
        <Tabs className="w-full space-y-6" value={activeTab} onValueChange={handleDashboardTabChange}>
          <TabsList className="sr-only">
            {ALL_DASHBOARD_TABS.map((tv) => (
              <TabsTrigger key={tv} value={tv} data-testid={`tab-trigger-${tv}`}>
                {tv}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={DASH_OVERVIEW_TAB} className="mt-0 focus-visible:outline-none" data-testid="dash-overview">
            <div className="overflow-hidden rounded-lg border border-slate-200/80 bg-white p-6 shadow-[0_24px_70px_-28px_rgba(15,23,42,0.14)] sm:p-8">
              <div className="space-y-3">
                <h2 className="font-display text-2xl font-black tracking-tighter text-[#0B0D12] sm:text-3xl md:text-[2.35rem] md:leading-tight">
                  Bonjour {prenom || " "}
                  <span className="text-[#2D7FF9]">.</span>
                </h2>
                <p className="max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
                  Votre activité en un coup d’œil — les actions urgentes et la marketplace sont à portée de clic.
                </p>
                    </div>

              <DashboardPriorityBanner items={priorityActions} onGoTab={handleDashboardTabChange} />

              <div
                className="mt-6 flex flex-col gap-3 rounded-xl border border-slate-200/90 bg-gradient-to-br from-slate-50 to-white p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5"
                data-testid="dash-overview-ctas"
              >
                <p className="text-sm font-medium text-slate-700 sm:max-w-xs">
                  Acheter ou vendre des dossiers qualifiés sur la marketplace.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row sm:shrink-0">
                    <Button
                    asChild
                    className="btn-primary h-11 w-full rounded-xl px-5 font-semibold shadow-sm shadow-[#2D7FF9]/20 sm:w-auto"
                    data-testid="dash-go-marketplace"
                  >
                    <Link to="/marketplace">
                      <MEMBER_GLOBAL_NAV_ICONS.marketplace className="mr-2 h-4 w-4 shrink-0" aria-hidden />
                      Marketplace
                    </Link>
                    </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="h-11 w-full rounded-xl border-slate-200 bg-white px-5 font-semibold hover:border-[#2D7FF9]/40 hover:bg-[#2D7FF9]/5 sm:w-auto"
                    data-testid="dash-go-publish"
                  >
                    <Link to="/nouveau-dossier">
                      <MEMBER_GLOBAL_NAV_ICONS.publish className="mr-2 h-4 w-4 shrink-0" aria-hidden />
                      Publier une annonce
                    </Link>
                  </Button>
                    </div>
                    </div>

              <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {overviewKpiCards.map(({ tab: kTab, label, value, hint, Icon }) => {
                  const urgent = priorityTabSet.has(kTab);
                  return (
                  <button
                    key={kTab}
                    type="button"
                    onClick={() => handleDashboardTabChange(kTab)}
                    className={cn(
                      "group flex w-full flex-col gap-3 rounded-xl border bg-gradient-to-br from-white to-slate-50/40 p-4 text-left shadow-sm transition-colors",
                      urgent
                        ? "border-amber-300/90 ring-1 ring-amber-200/60 hover:border-amber-400"
                        : "border-slate-200/90 hover:border-[#2D7FF9]/35 hover:to-white",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7FF9]/35"
                    )}
                    data-testid={`dash-kpi-${kTab}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-colors group-hover:bg-[#2D7FF9]/12 group-hover:text-[#2D7FF9]">
                        <Icon className="h-5 w-5" aria-hidden strokeWidth={2} />
                </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:translate-x-px group-hover:text-[#2D7FF9]" aria-hidden />
                </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
                      <div className="mt-1 font-display text-2xl font-black tabular-nums text-[#0B0D12]">{value}</div>
                      <div className="mt-1 text-xs leading-snug text-slate-500">{hint}</div>
                    </div>
                  </button>
                  );
                })}
              </div>

                    </div>
          </TabsContent>

          <TabsContent value="buys" className="mt-0 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm focus-visible:outline-none sm:p-5">
            <DashboardMarketplaceStrip testIdPrefix="dash-buys" />
            {displayBuys.length === 0 ? (
              buys.length > 0 ? (
                <EmptyState label="Les achats pour lesquels un litige s’est terminé en votre faveur (remboursement) n’apparaissent plus ici : consultez l’onglet Litiges." />
              ) : (
                <EmptyState
                  title="Aucun achat pour le moment"
                  label="Parcourez la marketplace pour réserver un créneau et sécuriser votre paiement."
                  cta={
                    <Button asChild className="btn-primary h-11 px-6">
                      <Link to="/marketplace">Parcourir la marketplace</Link>
                    </Button>
                  }
                />
              )
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Utilisez les boutons <strong>à droite de chaque ligne</strong> pour appeler, annuler, trancher le RDV ou noter.
                  Cliquez sur le reste de la ligne pour le détail (suivi, coordonnées, litige).
                </p>
                <ListShell
                  searchId="dash-buys-search"
                  searchPlaceholder="Annonce, lieu, état…"
                  searchValue={dashBuysQuery}
                  onSearchChange={(e) => setDashBuysQuery(e.target.value)}
                  count={displayBuys.length === 0 ? 0 : displayBuysFiltered.length}
                  total={displayBuys.length}
                  countNoun="achat"
                  emptyMessage={
                    displayBuys.length === 0
                      ? "Aucun achat."
                      : "Aucun achat ne correspond à la recherche."
                  }
                  listTestId="dash-buys-list"
                  columnGridClassName="sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,8rem)_auto_auto_1.25rem]"
                  columns={[
                    { key: "annonce", label: "Annonce", className: "sm:col-start-1" },
                    { key: "montant", label: "Montant", className: "sm:col-start-2 text-right" },
                    { key: "situation", label: "Fonds / délai", className: "sm:col-start-3" },
                    { key: "etat", label: "État", className: "sm:col-start-4" },
                    { key: "actions", label: "Actions", className: "sm:col-start-5 text-center" },
                    { key: "go", label: "", className: "sm:col-start-6" },
                  ]}
                  minHeightClass="min-h-0"
                >
                  {displayBuysFiltered.map((t) => {
                    const st = statusLabels[t.statut] || {};
                    const txKey = normTxId(t.transaction_id ?? t.id);
                    const litigesForRow =
                      Array.isArray(t.litiges) && t.litiges.length
                        ? t.litiges
                        : disputes.filter((d) => normTxId(d?.transaction_id) === txKey);
                    const rowUnlocked = buyerPrivateUnlocked(t, litigesForRow);
                    const tr = resolveTransactionTracking(t, { litiges: litigesForRow, role: "buyer" });
                    return (
                      <ListRow
                        key={txKey || t.transaction_id || t.id}
                        testId={`buy-row-${t.transaction_id ?? t.id}`}
                        gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,8rem)_auto_1.25rem] sm:items-center sm:gap-2"
                        chevronClassName="sm:col-start-4 sm:justify-self-end"
                        actions={renderBuyQuickActions(t)}
                        onClick={() => {
                          setDashBuyPane("main");
                          setDashBuyDetailTx(txKey);
                        }}
                        className={
                          dashBuyDetailTx === txKey
                            ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300"
                            : undefined
                        }
                      >
                        <div className="min-w-0 sm:col-span-1">
                          <div className="truncate text-sm font-semibold text-slate-900">{t.dossier_title}</div>
                          <div className="text-[11px] text-slate-500">
                            {new Date(t.created_at).toLocaleDateString("fr-FR")}
                  </div>
                    </div>
                        <div className="shrink-0 text-right text-sm font-bold tabular-nums sm:col-span-1">
                          {Number(t.buyer_total).toLocaleString("fr-FR")}€
                  </div>
                        <div className="hidden min-w-0 sm:block sm:col-span-1">
                          <div className="truncate text-[11px] text-slate-600">{tr.pipeline_label}</div>
                          <div className="truncate text-[10px] text-slate-500">
                            <TrackingListSubline tracking={tr} role="buyer" tx={t} />
                </div>
                        </div>
                        <Badge className={cn("shrink-0 w-fit", st.className)}>{st.label}</Badge>
                      </ListRow>
                    );
                  })}
                </ListShell>
              </div>
            )}
          </TabsContent>

          <TabsContent value="sales" className="mt-0 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm focus-visible:outline-none sm:p-5">
            <DashboardMarketplaceStrip testIdPrefix="dash-sales" />

            <div
              className="mb-4 rounded-xl border border-slate-200 bg-slate-50/90 px-4 py-3 text-xs leading-relaxed text-slate-700"
              data-testid="seller-payout-explainer"
            >
              <p className="font-semibold text-slate-900 mb-1.5">Comment être payé sur votre compte ?</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>
                  L&apos;acheteur <strong>confirme le RDV</strong> → votre gain est <strong>validé</strong> (plus bloqué
                  chez l&apos;acheteur).
                </li>
                <li>
                  Délai de sécurité <strong>{SELLER_PAYOUT_HOURS} h</strong> (sauf litige).
                </li>
                <li>
                  <strong>Pièce d&apos;identité + IBAN</strong> (bouton en haut à droite) → Stripe verse sur votre compte.
                </li>
              </ol>
              <p className="mt-2 text-slate-600">
                Tant que l&apos;IBAN n&apos;est pas configuré, le gain reste <strong>validé</strong> mais{" "}
                <strong>pas versé</strong> sur votre banque.
              </p>
              <p className="mt-2 text-slate-600 border-t border-slate-200 pt-2">
                <strong>En cas de litige</strong> (acheteur ou équipe RdvMarket) : le versement est{" "}
                <strong>gelé</strong> jusqu&apos;à la décision. Si l&apos;acheteur a raison → remboursement, vous ne
                touchez pas le montant. Si vous avez raison → versement reprend (IBAN + délai déjà écoulé si applicable).
              </p>
            </div>

            {sellerDisputeHoldEur > 0 ? (
              <div
                className="mb-4 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-950"
                data-testid="seller-payout-dispute-banner"
              >
                <strong>{sellerDisputeHoldEur.toLocaleString("fr-FR")} €</strong> bloqué(s) : un litige est ouvert sur
                au moins une vente. Aucun versement Stripe ne part tant que l&apos;équipe n&apos;a pas tranché (onglet{" "}
                <strong>Litiges</strong>).
              </div>
              ) : null}

            {!payoutsReady && sellerPendingPayoutEur > 0 ? (
              <div
                className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                data-testid="seller-payout-setup-banner"
              >
                <p className="text-sm text-amber-950">
                  <strong>{sellerPendingPayoutEur.toLocaleString("fr-FR")} €</strong> de gains validés ne peuvent pas
                  être versés tant que vous n&apos;avez pas complété{" "}
                  <strong>identité + coordonnées bancaires</strong>.
                </p>
                <Button
                  type="button"
                  size="sm"
                  className="btn-primary shrink-0"
                  onClick={() => sellerWallet.handleClick()}
                >
                  Configurer les versements
                </Button>
            </div>
            ) : null}

            {sales.length === 0 ? (
              <EmptyState label="Aucune vente pour le moment" />
            ) : (
              <div className="mb-6 space-y-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm font-semibold text-slate-800">Filtrer vos ventes</p>
                  <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
                    <button
                      type="button"
                      onClick={() => setSalesListFilter("toutes")}
                      aria-pressed={salesListFilter === "toutes"}
                      className={cn(
                        "inline-flex h-10 min-w-[5.75rem] shrink-0 items-center justify-center whitespace-nowrap rounded-xl px-3 text-xs font-semibold tabular-nums transition sm:px-4 sm:text-sm",
                        salesListFilter === "toutes"
                          ? "bg-[#2D7FF9] text-white shadow-sm"
                          : "bg-white text-slate-900 ring-1 ring-slate-200/80 hover:bg-slate-50",
                      )}
                      data-testid="sales-filter-toutes"
                    >
                      Toutes ({sales.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSalesListFilter("en_cours")}
                      aria-pressed={salesListFilter === "en_cours"}
                      className={cn(
                        "inline-flex h-10 min-w-[5.75rem] shrink-0 items-center justify-center whitespace-nowrap rounded-xl px-3 text-xs font-semibold tabular-nums transition sm:px-4 sm:text-sm",
                        salesListFilter === "en_cours"
                          ? "bg-[#2D7FF9] text-white shadow-sm"
                          : "bg-white text-slate-900 ring-1 ring-slate-200/80 hover:bg-slate-50",
                      )}
                      data-testid="sales-filter-en-cours"
                    >
                      En cours ({sellerSalesOpenCount})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSalesListFilter("a_recevoir")}
                      aria-pressed={salesListFilter === "a_recevoir" || salesListFilter === "encaisse"}
                      className={cn(
                        "inline-flex h-10 min-w-[6.5rem] shrink-0 items-center justify-center whitespace-nowrap rounded-xl px-3 text-xs font-semibold tabular-nums transition sm:px-4 sm:text-sm",
                        salesListFilter === "a_recevoir" || salesListFilter === "encaisse"
                          ? "bg-[#2D7FF9] text-white shadow-sm"
                          : "bg-white text-slate-900 ring-1 ring-slate-200/80 hover:bg-slate-50",
                      )}
                      data-testid="sales-filter-a-recevoir"
                    >
                      À recevoir ({sellerSalesToReceiveCount})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSalesListFilter("versees")}
                      aria-pressed={salesListFilter === "versees"}
                      className={cn(
                        "inline-flex h-10 min-w-[5.5rem] shrink-0 items-center justify-center whitespace-nowrap rounded-xl px-3 text-xs font-semibold tabular-nums transition sm:px-4 sm:text-sm",
                        salesListFilter === "versees"
                          ? "bg-[#2D7FF9] text-white shadow-sm"
                          : "bg-white text-slate-900 ring-1 ring-slate-200/80 hover:bg-slate-50",
                      )}
                      data-testid="sales-filter-versees"
                    >
                      Versées ({sellerSalesPaidOutCount})
                    </button>
                  </div>
                </div>
                {filteredSales.length === 0 ? (
                  <EmptyState
                    label={
                      salesListFilter === "a_recevoir" || salesListFilter === "encaisse"
                        ? "Aucun gain validé en attente de versement"
                        : salesListFilter === "versees"
                          ? "Aucune vente encore versée sur votre compte Stripe"
                        : salesListFilter === "en_cours"
                          ? "Aucune vente en cours dans cette catégorie"
                          : "Aucune ligne à afficher"
                    }
                  />
                ) : (
                  <div className="space-y-3">
                    <ListShell
                      searchId="dash-sales-search"
                      searchPlaceholder="Annonce, montant, état…"
                      searchValue={dashSalesQuery}
                      onSearchChange={(e) => setDashSalesQuery(e.target.value)}
                      count={filteredSales.length === 0 ? 0 : filteredSalesSearched.length}
                      total={filteredSales.length}
                      countNoun="vente"
                      emptyMessage={
                        filteredSales.length === 0
                          ? "Aucune vente."
                          : "Aucune vente ne correspond à la recherche."
                      }
                      listTestId="dash-sales-list"
                      columnGridClassName="sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,9rem)_auto_1.25rem]"
                      columns={[
                        { key: "annonce", label: "Annonce", className: "sm:col-start-1" },
                        { key: "montant", label: "Net", className: "sm:col-start-2 text-right" },
                        { key: "versement", label: "Versement", className: "sm:col-start-3" },
                        { key: "etat", label: "État", className: "sm:col-start-4" },
                        { key: "go", label: "", className: "sm:col-start-5" },
                      ]}
                      minHeightClass="min-h-0"
                    >
                      {filteredSalesSearched.map((t) => {
                      const st = sellerSaleStatusBadge(t, { payoutsReady });
                      const saleTxKey = normTxId(t.transaction_id ?? t.id);
                        const amount = t.seller_net ?? t.seller_payout ?? t.price ?? t.buyer_total;
                        const saleLitiges =
                          Array.isArray(t.litiges) && t.litiges.length
                            ? t.litiges
                            : disputes.filter((d) => normTxId(d?.transaction_id) === saleTxKey);
                        const trSale = resolveTransactionTracking(t, { litiges: saleLitiges, role: "seller" });
                      return (
                          <ListRow
                            key={saleTxKey || t.transaction_id || t.id}
                            testId={`sale-row-${t.transaction_id ?? t.id}`}
                            gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,9rem)_auto_1.25rem] sm:items-center sm:gap-2"
                            onClick={() => {
                              setDashSalePane("main");
                              setDashSaleDetailTx(saleTxKey);
                            }}
                            className={
                              dashSaleDetailTx === saleTxKey
                                ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300"
                                : undefined
                            }
                          >
                            <div className="min-w-0 sm:col-span-1">
                              <div className="truncate text-sm font-semibold text-slate-900">{t.dossier_title}</div>
                              <div className="text-[11px] text-slate-500">
                                {new Date(t.created_at).toLocaleDateString("fr-FR")}
                          </div>
                            </div>
                            <div className="shrink-0 text-right text-sm font-bold tabular-nums sm:col-span-1">
                              {amount != null && amount !== "" ? `${Number(amount).toLocaleString("fr-FR")}€` : "—"}
                          </div>
                            <div className="hidden min-w-0 sm:block sm:col-span-1">
                              <div className="truncate text-[11px] text-slate-600">{trSale.pipeline_label}</div>
                              <div className="truncate text-[10px] text-slate-500">
                                <TrackingListSubline tracking={trSale} role="seller" tx={t} />
                        </div>
                            </div>
                            <Badge className={cn("shrink-0 w-fit", st.className)}>{st.label}</Badge>
                          </ListRow>
                      );
                    })}
                    </ListShell>
                  </div>
                )}
              </div>
            )}

            {sales.length > 0 ? (
              <p className="mt-2 max-w-2xl text-xs leading-relaxed text-slate-500">
                <strong>À recevoir</strong> = RDV confirmé, argent pas encore sur votre banque (délai {SELLER_PAYOUT_HOURS}{" "}
                h + IBAN). <strong>Versées</strong> = transfert Stripe effectué. Vous pouvez{" "}
                <strong>noter l’acheteur</strong> une fois le gain validé ou la vente remboursée.
              </p>
            ) : null}
          </TabsContent>

          <TabsContent value="annonces" className="mt-0 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm focus-visible:outline-none sm:p-5">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-semibold text-slate-800">Vos annonces</p>
              <div className="flex w-full max-w-full items-center gap-2 sm:w-auto">
                <div className="grid w-full grid-cols-2 gap-2 rounded-2xl border border-slate-200/90 bg-slate-50/70 p-2 sm:w-[20rem]">
                  <button
                    type="button"
                    onClick={() => setAnnonceFilter("vente")}
                    aria-pressed={annonceFilter === "vente"}
                    className={cn(
                      "h-10 rounded-xl text-sm font-semibold transition",
                      annonceFilter === "vente"
                        ? "bg-[#2D7FF9] text-white shadow-sm"
                        : "bg-white text-slate-900 hover:bg-slate-50 ring-1 ring-slate-200/80"
                    )}
                    data-testid="annonces-filter-vente"
                  >
                    Ce que je vends
                  </button>
                  <button
                    type="button"
                    onClick={() => setAnnonceFilter("recherche")}
                    aria-pressed={annonceFilter === "recherche"}
                    className={cn(
                      "h-10 rounded-xl text-sm font-semibold transition",
                      annonceFilter === "recherche"
                        ? "bg-[#2D7FF9] text-white shadow-sm"
                        : "bg-white text-slate-900 hover:bg-slate-50 ring-1 ring-slate-200/80"
                    )}
                    data-testid="annonces-filter-recherche"
                  >
                    Ce que j’achète
                  </button>
                </div>
              </div>
            </div>
            {!annoncesLoaded ? (
              <EmptyState label="Chargement de vos annonces…" />
            ) : annoncesLoadError ? (
              <EmptyState
                label={`Impossible de charger vos annonces. ${annoncesLoadError}`}
                cta={
                  <Button variant="outline" onClick={load} data-testid="retry-load-annonces">
                    Réessayer
                  </Button>
                }
              />
            ) : myAnnonces.length === 0 ? (
              <EmptyState
                label="Vous n’avez pas encore publié d’annonce."
                cta={
                  <Button asChild className="btn-primary">
                    <Link to="/nouveau-dossier">Publier une annonce</Link>
                  </Button>
                }
              />
            ) : myAnnoncesByType.length === 0 ? (
                    <EmptyState
                      label={
                        annonceFilter === "vente"
                          ? "Aucune annonce en vente pour le moment."
                          : "Aucune annonce d’achat (recherche) pour le moment."
                      }
                      cta={
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          {annonceFilter === "vente" ? (
                            <Button variant="outline" onClick={() => setAnnonceFilter("recherche")}>
                              Voir ce que j’achète
                            </Button>
                          ) : (
                            <Button variant="outline" onClick={() => setAnnonceFilter("vente")}>
                              Voir ce que je vends
                            </Button>
                          )}
                        </div>
                      }
                    />
            ) : (
                  <>
                    <p className="mb-3 text-sm text-slate-600">
                      Cliquez une ligne pour modifier, partager ou supprimer une annonce.
                    </p>
                    <ListShell
                      searchId="dash-annonces-search"
                      searchPlaceholder="Titre, lieu, statut…"
                      searchValue={dashAnnoncesQuery}
                      onSearchChange={(e) => setDashAnnoncesQuery(e.target.value)}
                      count={myAnnoncesByType.length === 0 ? 0 : myAnnoncesFiltered.length}
                      total={myAnnoncesByType.length}
                      countNoun="annonce"
                      emptyMessage={
                        myAnnoncesByType.length === 0
                          ? "Aucune annonce dans ce filtre."
                          : "Aucune annonce ne correspond à la recherche."
                      }
                      listTestId="dash-annonces-list"
                      columnGridClassName="sm:grid-cols-[minmax(0,1fr)_auto_1.25rem]"
                      columns={[
                        { key: "titre", label: "Annonce", className: "sm:col-start-1" },
                        { key: "prix", label: "Prix", className: "sm:col-start-2 text-right" },
                        { key: "go", label: "", className: "sm:col-start-3" },
                      ]}
                      minHeightClass="min-h-0"
                    >
                      {myAnnoncesFiltered.map((d) => {
                        const rowKey = String(d.dossier_id);
                        const title = d.titre || d.title || "Sans titre";
                        const isRechercheRow = normalizeAnnonceType(d.type) === "recherche";
                        const price =
                          isRechercheRow && d.price_min != null && d.price_max != null
                            ? `${Number(d.price_min).toLocaleString("fr-FR")} – ${Number(d.price_max).toLocaleString("fr-FR")} €`
                            : d.price != null && d.price !== ""
                              ? `${Number(d.price).toLocaleString("fr-FR")} €`
                              : "—";
                        const annonceSt = annonceDossierStatusBadge(d.status || d.statut);
                return (
                          <ListRow
                            key={rowKey}
                            testId={`my-annonce-row-${rowKey}`}
                            gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_auto_1.25rem] sm:items-center sm:gap-2"
                            onClick={() => {
                              setDashAnnonceDetailId(rowKey);
                              setDashAnnoncePane("dossier");
                            }}
                            className={
                              dashAnnonceDetailId === rowKey
                                ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300"
                                : undefined
                            }
                          >
                            <div className="min-w-0 sm:col-span-1">
                              <div className="truncate text-sm font-semibold text-slate-900">{title}</div>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                <span
                                  className={cn(
                                    "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
                                    annonceSt.className,
                                  )}
                                >
                                  {annonceSt.label}
                                </span>
                                <DeptLocationPills dossier={d} size="xs" />
                        </div>
                  </div>
                            <div className="shrink-0 text-sm font-bold tabular-nums text-slate-900 sm:col-span-1">{price}</div>
                          </ListRow>
                );
                      })}
                    </ListShell>
                  </>
            )}
          </TabsContent>

          <TabsContent value="suggestions" className="mt-0 focus-visible:outline-none">
            {rechercheProposals.length === 0 ? (
              <EmptyState
                label="Aucune proposition pour le moment. Les vendeurs dont la fiche correspond à votre annonce « Je cherche » peuvent envoyer des suggestions depuis le bouton Proposer sur la fiche."
              />
            ) : (
              <>
                <p className="mb-3 text-sm text-slate-600">
                  Propositions reçues sur vos annonces « Je cherche ». Cliquez une ligne pour accepter ou refuser.
                </p>
                <ListShell
                  searchId="dash-suggestions-search"
                  searchPlaceholder="Demande, fiche proposée, état…"
                  searchValue={dashSuggestionsQuery}
                  onSearchChange={(e) => setDashSuggestionsQuery(e.target.value)}
                  count={rechercheProposals.length === 0 ? 0 : rechercheProposalsFiltered.length}
                  total={rechercheProposals.length}
                  countNoun="suggestion"
                  emptyMessage={
                    rechercheProposals.length === 0
                      ? "Aucune proposition."
                      : "Aucune proposition ne correspond à la recherche."
                  }
                  listTestId="dash-suggestions-list"
                  columnGridClassName="sm:grid-cols-[minmax(0,1fr)_auto_1.25rem]"
                  columns={[
                    { key: "demande", label: "Demande / fiche", className: "sm:col-start-1" },
                    { key: "etat", label: "État", className: "sm:col-start-2" },
                    { key: "go", label: "", className: "sm:col-start-3" },
                  ]}
                  minHeightClass="min-h-0"
                >
                  {rechercheProposalsFiltered.map((pr) => {
                    const rowKey = String(pr.proposal_id);
                  const rechercheTitle =
                      pr?.recherche?.titre ||
                      pr?.recherche?.title ||
                      (pr?.recherche_dossier_id ? `Demande ${String(pr.recherche_dossier_id).slice(0, 8)}…` : "Demande");
                  const venteTitle =
                      pr?.vente?.titre ||
                      pr?.vente?.title ||
                      (pr?.vente_dossier_id ? `Fiche ${String(pr.vente_dossier_id).slice(0, 8)}…` : "Fiche vente");
                    const st =
                      pr.status === "pending"
                        ? { label: "En attente", cls: "bg-amber-100 text-amber-900" }
                        : pr.status === "accepted"
                          ? { label: "Acceptée", cls: "bg-emerald-100 text-emerald-800" }
                          : { label: "Refusée", cls: "bg-slate-200 text-slate-700" };
                  return (
                      <ListRow
                        key={rowKey}
                        testId={`proposal-row-${rowKey}`}
                        gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_auto_1.25rem] sm:items-center sm:gap-2"
                        onClick={() => {
                          setDashProposalPane("main");
                          setDashProposalDossierId(null);
                          setDashProposalId(rowKey);
                        }}
                        className={
                          dashProposalId === rowKey
                            ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300"
                            : undefined
                        }
                      >
                        <div className="min-w-0 sm:col-span-1">
                          <div className="truncate text-sm font-semibold text-slate-900">{rechercheTitle}</div>
                          <div className="truncate text-[11px] text-slate-600">→ {venteTitle}</div>
                        </div>
                        <Badge className={cn("shrink-0", st.cls)}>{st.label}</Badge>
                      </ListRow>
                  );
                })}
                </ListShell>
              </>
            )}
          </TabsContent>

          <TabsContent value="negotiations" className="mt-0 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm focus-visible:outline-none sm:p-5">
            {negotiations.length === 0 ? <EmptyState label="Aucune négociation en cours" /> : (
              <div className="space-y-3">
                <p className="mb-3 text-sm text-slate-600">
                  Cliquez une ligne pour voir les offres, répondre ou payer.
                </p>
                <ListShell
                  searchId="dash-negotiations-search"
                  searchPlaceholder="Annonce, offre, état…"
                  searchValue={dashNegotiationsQuery}
                  onSearchChange={(e) => setDashNegotiationsQuery(e.target.value)}
                  count={negotiations.length === 0 ? 0 : negotiationsFiltered.length}
                  total={negotiations.length}
                  countNoun="négociation"
                  emptyMessage={
                    negotiations.length === 0
                      ? "Aucune négociation."
                      : "Aucune négociation ne correspond à la recherche."
                  }
                  listTestId="dash-negotiations-list"
                  columnGridClassName="sm:grid-cols-[minmax(0,1fr)_auto_1.25rem]"
                  columns={[
                    { key: "annonce", label: "Annonce", className: "sm:col-start-1" },
                    { key: "etat", label: "État", className: "sm:col-start-2" },
                    { key: "go", label: "", className: "sm:col-start-3" },
                  ]}
                  minHeightClass="min-h-0"
                >
                  {negotiationsFiltered.map((n) => {
                  const dossier = n?.dossiers || n?.dossier || null;
                  const dossierTitle = n?.dossier_title ?? dossier?.title ?? dossier?.titre ?? "Annonce";
                    const rowKey = String(n.negotiation_id);
                  const lastOffer = n.offers?.[n.offers.length - 1];
                  return (
                      <ListRow
                        key={rowKey}
                        testId={`neg-row-${rowKey}`}
                        gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_auto_1.25rem] sm:items-center sm:gap-2"
                        onClick={() => {
                          setDashNegPane("main");
                          setDashNegDetailId(rowKey);
                        }}
                        className={
                          dashNegDetailId === rowKey ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300" : undefined
                        }
                      >
                        <div className="min-w-0 sm:col-span-1">
                          <div className="truncate text-sm font-semibold text-slate-900">{dossierTitle}</div>
                          <div className="text-[11px] text-slate-500">
                            {lastOffer
                              ? `Dernière offre : ${Number(lastOffer.amount).toLocaleString("fr-FR")}€`
                              : `${n.offers?.length || 0} offre(s)`}
                          </div>
                        </div>
                        <Badge
                          className={cn(
                            "shrink-0",
                            n.status === "accepted"
                              ? "bg-emerald-100 text-emerald-800"
                              : n.status === "open"
                                ? "bg-amber-100 text-amber-900"
                                : "bg-slate-200 text-slate-700",
                          )}
                        >
                          {negotiationStatusLabelFr(n.status)}
                        </Badge>
                      </ListRow>
                    );
                  })}
                </ListShell>
                        </div>
            )}
          </TabsContent>

          <TabsContent value="disputes" className="mt-0 focus-visible:outline-none">
            {disputes.length === 0 ? (
              <EmptyState label="Aucun litige en cours" />
            ) : (
              <>
                <p className="mb-3 text-sm text-slate-600">
                  Un litige ouvert est <strong>en examen par l’équipe RdvMarket</strong>. Cliquez une ligne pour le détail
                  (motif, statut, décision le cas échéant).
                </p>
                <ListShell
                  searchId="dash-disputes-search"
                  searchPlaceholder="Annonce, motif, état…"
                  searchValue={dashDisputesQuery}
                  onSearchChange={(e) => setDashDisputesQuery(e.target.value)}
                  count={disputes.length === 0 ? 0 : disputesFiltered.length}
                  total={disputes.length}
                  countNoun="litige"
                  emptyMessage={
                    disputes.length === 0 ? "Aucun litige." : "Aucun litige ne correspond à la recherche."
                  }
                  listTestId="dash-disputes-list"
                  columnGridClassName="sm:grid-cols-[minmax(0,1fr)_auto_1.25rem]"
                  columns={[
                    { key: "annonce", label: "Annonce", className: "sm:col-start-1" },
                    { key: "etat", label: "État", className: "sm:col-start-2" },
                    { key: "go", label: "", className: "sm:col-start-3" },
                  ]}
                  minHeightClass="min-h-0"
                >
                  {disputesFiltered.map((d) => {
                    const disputeKey = d?.dispute_id ?? d?.id ?? `${d?.transaction_id || "litige"}_${d?.created_at || ""}`;
                    const rowKey = String(disputeKey);
                    const txId = normTxId(d?.transaction_id);
                    const tx =
                      (txId && buys.find((b) => normTxId(b?.transaction_id ?? b?.id) === txId)) ||
                      (txId && sales.find((s) => normTxId(s?.transaction_id ?? s?.id) === txId)) ||
                      null;
                    const dossierTitle = disputeListingTitle(d, buys, sales);
                    const rawStatus = String(d?.status ?? d?.statut ?? "open").trim().toLowerCase();
                    const metaParts = [
                      d?.reason ? String(d.reason).trim() : null,
                      d?.transaction_amount != null && Number.isFinite(Number(d.transaction_amount))
                        ? `${Number(d.transaction_amount).toLocaleString("fr-FR")} €`
                        : null,
                      d?.created_at ? new Date(d.created_at).toLocaleDateString("fr-FR") : null,
                    ].filter(Boolean);
                    return (
                      <ListRow
                        key={rowKey}
                        testId={`user-dispute-row-${rowKey}`}
                        gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_auto_1.25rem] sm:items-center sm:gap-2"
                        onClick={() => {
                          setDashDisputePane("main");
                          setDashDisputeDossierId(null);
                          setDashDisputeDetailKey(rowKey);
                        }}
                        className={
                          dashDisputeDetailKey === rowKey
                            ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300"
                            : undefined
                        }
                      >
                        <div className="min-w-0 sm:col-span-1">
                          <div className="truncate text-sm font-semibold text-slate-900">{dossierTitle}</div>
                          <div className="truncate text-[11px] text-slate-500">{metaParts.join(" · ")}</div>
                        </div>
                        <Badge
                          className={cn(
                            "shrink-0 sm:justify-self-center",
                            rawStatus === "open" || rawStatus === "in_review"
                              ? "bg-orange-100 text-orange-800"
                              : "bg-slate-200",
                          )}
                        >
                          {litigeRowStatusLabelFr(d?.status ?? d?.statut ?? "open")}
                        </Badge>
                      </ListRow>
                    );
                  })}
                </ListShell>
              </>
            )}
          </TabsContent>

          <TabsContent value="invoices" className="mt-0 focus-visible:outline-none">
            {invoices.length === 0 ? (
              <EmptyState label="Aucune facture pour le moment" />
            ) : (
              <>
                <p className="mb-3 text-sm text-slate-600">Cliquez une ligne pour le détail et télécharger le PDF.</p>
                <ListShell
                  searchId="dash-invoices-search"
                  searchPlaceholder="N° facture, libellé…"
                  searchValue={dashInvoicesQuery}
                  onSearchChange={(e) => setDashInvoicesQuery(e.target.value)}
                  count={invoices.length === 0 ? 0 : invoicesFiltered.length}
                  total={invoices.length}
                  countNoun="facture"
                  emptyMessage={
                    invoices.length === 0
                      ? "Aucune facture."
                      : "Aucune facture ne correspond à la recherche."
                  }
                  listTestId="dash-invoices-list"
                  columnGridClassName="sm:grid-cols-[minmax(0,1fr)_auto_1.25rem]"
                  columns={[
                    { key: "label", label: "Document", className: "sm:col-start-1" },
                    { key: "montant", label: "Montant", className: "sm:col-start-2 text-right" },
                    { key: "go", label: "", className: "sm:col-start-3" },
                  ]}
                  minHeightClass="min-h-0"
                >
                  {invoicesFiltered.map((inv) => {
                    const rowKey = String(inv.invoice_number);
                    return (
                      <ListRow
                        key={rowKey}
                        testId={`invoice-row-${rowKey}`}
                        gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_auto_1.25rem] sm:items-center sm:gap-2"
                        onClick={() => setDashInvoiceNumber(rowKey)}
                        className={
                          dashInvoiceNumber === rowKey
                            ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300"
                            : undefined
                        }
                      >
                        <div className="min-w-0 sm:col-span-1">
                          <div className="truncate text-sm font-semibold text-slate-900">{inv.label}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {inv.invoice_number} · {new Date(inv.date).toLocaleDateString("fr-FR")}
                        </div>
                    </div>
                        <div className="shrink-0 text-right text-sm font-bold tabular-nums text-slate-900 sm:col-span-1">
                          {Number(inv.amount_ht).toLocaleString("fr-FR")} € HT
                        </div>
                      </ListRow>
                  );
                })}
                </ListShell>
              </>
            )}
          </TabsContent>

        </Tabs>

        </div>
      </MemberShell>


      <DetailDialog
        open={Boolean(dashBuyDetailTx) && !inlineCallTx}
        onOpenChange={(open) => {
          if (!open) {
            setDashBuyDetailTx(null);
            setDashBuyPane("main");
          }
        }}
        onBack={dashBuyPane === "dossier" ? () => setDashBuyPane("main") : undefined}
        size="xl"
        testId="dash-buy-detail-dialog"
        title={dashBuyPane === "dossier" ? "Fiche annonce" : dashBuyDetailRow?.dossier_title || "Achat"}
        description={
          dashBuyPane === "main" && dashBuyDetailRow?.created_at
            ? new Date(dashBuyDetailRow.created_at).toLocaleString("fr-FR")
            : undefined
        }
      >
        {dashBuyPane === "dossier" && dashBuyDetailRow?.dossier_id ? (
          <ModalDossierEmbed dossierId={dashBuyDetailRow.dossier_id} onBack={() => setDashBuyPane("main")} />
        ) : (
          renderBuyTransactionDetail(dashBuyDetailRow, { onOpenDossier: () => setDashBuyPane("dossier") })
        )}
      </DetailDialog>
      <DetailDialog
        open={Boolean(dashSaleDetailTx)}
        onOpenChange={(open) => {
          if (!open) {
            setDashSaleDetailTx(null);
            setDashSalePane("main");
          }
        }}
        onBack={dashSalePane === "dossier" ? () => setDashSalePane("main") : undefined}
        size="lg"
        testId="dash-sale-detail-dialog"
        title={dashSalePane === "dossier" ? "Fiche annonce" : dashSaleDetailRow?.dossier_title || "Vente"}
        description={
          dashSalePane === "main" && dashSaleDetailRow?.created_at
            ? new Date(dashSaleDetailRow.created_at).toLocaleString("fr-FR")
            : undefined
        }
      >
        {dashSalePane === "dossier" && dashSaleDetailRow?.dossier_id ? (
          <ModalDossierEmbed dossierId={dashSaleDetailRow.dossier_id} onBack={() => setDashSalePane("main")} />
        ) : (
          renderSaleTransactionDetail(dashSaleDetailRow, { onOpenDossier: () => setDashSalePane("dossier") })
        )}
      </DetailDialog>
      <DetailDialog
        open={Boolean(dashDisputeDetailKey)}
        onOpenChange={(open) => {
          if (!open) {
            setDashDisputeDetailKey(null);
            setDashDisputePane("main");
            setDashDisputeDossierId(null);
          }
        }}
        onBack={dashDisputePane === "dossier" ? () => setDashDisputePane("main") : undefined}
        testId="dash-dispute-detail-dialog"
        title={
          dashDisputePane === "dossier"
            ? "Fiche annonce"
            : dashDisputeDetailRow
              ? disputeListingTitle(dashDisputeDetailRow, buys, sales)
              : "Litige"
        }
      >
        {dashDisputePane === "dossier" && dashDisputeDossierId ? (
          <ModalDossierEmbed dossierId={dashDisputeDossierId} onBack={() => setDashDisputePane("main")} />
        ) : dashDisputeDetailRow ? (
          (() => {
            const d = dashDisputeDetailRow;
                  const txId = normTxId(d?.transaction_id);
                  const tx =
                    (txId && buys.find((b) => normTxId(b?.transaction_id ?? b?.id) === txId)) ||
                    (txId && sales.find((s) => normTxId(s?.transaction_id ?? s?.id) === txId)) ||
                    null;
            const dossierId = d?.dossier_id ?? tx?.dossier_id ?? null;
            const listingTitle = disputeListingTitle(d, buys, sales);
                  const rawStatus = String(d?.status ?? d?.statut ?? "open").trim().toLowerCase();
                  const decision = String(d?.admin_decision || "").trim().toLowerCase();
                  const decisionExplained =
                    decision === "refund" || decision === "release" ? litigeDecisionExplainedFr(d.admin_decision) : null;
                  const resolvedAt = d?.resolved_at ? new Date(d.resolved_at).toLocaleString("fr-FR") : null;
                  return (
              <div className="space-y-4">
                <ModalSummaryBar
                  badge={
                          <Badge
                            className={cn(
                        rawStatus === "open" || rawStatus === "in_review"
                          ? "bg-orange-100 text-orange-800"
                          : "bg-slate-200",
                            )}
                          >
                      {litigeRowStatusLabelFr(d?.status ?? d?.statut ?? "open")}
                          </Badge>
                  }
                  amount={
                    d?.transaction_amount != null && Number.isFinite(Number(d.transaction_amount))
                      ? `${Number(d.transaction_amount).toLocaleString("fr-FR")}€`
                      : null
                  }
                  meta={listingTitle}
                />
                        {dossierId ? (
                  <button
                    type="button"
                    className="text-sm font-semibold text-[#2D7FF9] hover:underline"
                    data-testid="dash-dispute-open-dossier"
                    onClick={() => {
                      setDashDisputeDossierId(dossierId);
                      setDashDisputePane("dossier");
                    }}
                  >
                    Voir la fiche annonce →
                  </button>
                        ) : null}
                <ModalSection title="Motif">
                  <p className="text-sm text-slate-800">{d?.reason || "—"}</p>
                          {d?.description ? (
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{d.description}</p>
                          ) : null}
                </ModalSection>
                          {rawStatus === "closed" ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
                    <p className="text-[10px] font-semibold uppercase text-slate-500">Résolution</p>
                    <p className="mt-1">{decisionExplained || "Décision enregistrée."}</p>
                    {resolvedAt ? <p className="mt-1 text-xs text-slate-500">Clôturé le {resolvedAt}</p> : null}
                            </div>
                          ) : (
                  <p className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm text-amber-950">
                    En cours de traitement par l’équipe RdvMarket.
                  </p>
                )}
                              </div>
            );
          })()
                          ) : null}
      </DetailDialog>
      <DetailDialog
        open={Boolean(dashInvoiceNumber)}
        onOpenChange={(open) => { if (!open) setDashInvoiceNumber(null); }}
        testId="dash-invoice-detail-dialog"
        title={dashInvoiceDetailRow?.label || "Facture"}
        description={dashInvoiceDetailRow?.invoice_number}
      >
        {dashInvoiceDetailRow ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {new Date(dashInvoiceDetailRow.date).toLocaleDateString("fr-FR")} ·{" "}
              {dashInvoiceDetailRow.kind === "buyer_purchase" ? "Achat" : "Abonnement"}
            </p>
            <p className="font-display text-2xl font-bold tabular-nums">{Number(dashInvoiceDetailRow.amount_ht).toLocaleString("fr-FR")} € HT</p>
            <Button size="sm" variant="outline" onClick={() => downloadInvoice(dashInvoiceDetailRow.kind, dashInvoiceDetailRow.transaction_id)}>
              <Download className="w-4 h-4 mr-1" />Télécharger le PDF
            </Button>
                        </div>
                      ) : null}
      </DetailDialog>
      <DetailDialog
        open={Boolean(dashProposalId)}
        onOpenChange={(open) => {
          if (!open) {
            setDashProposalId(null);
            setDashProposalPane("main");
            setDashProposalDossierId(null);
          }
        }}
        onBack={dashProposalPane === "dossier" ? () => setDashProposalPane("main") : undefined}
        testId="dash-proposal-detail-dialog"
        title={dashProposalPane === "dossier" ? "Fiche annonce" : "Suggestion reçue"}
      >
        {dashProposalPane === "dossier" && dashProposalDossierId ? (
          <ModalDossierEmbed dossierId={dashProposalDossierId} onBack={() => setDashProposalPane("main")} />
        ) : dashProposalDetailRow ? (() => {
          const p = dashProposalDetailRow;
          const rechercheTitle = p?.recherche?.titre || p?.recherche?.title || "Demande";
          const venteTitle = p?.vente?.titre || p?.vente?.title || "Fiche vente";
          const ventePrice = p?.vente?.price != null ? `${Number(p.vente.price).toLocaleString("fr-FR")}€` : null;
          return (
            <div className="space-y-4">
              <p className="text-sm"><strong>Votre demande :</strong> {rechercheTitle}</p>
              <p className="text-sm"><strong>Fiche proposée :</strong> {venteTitle}{ventePrice ? ` · ${ventePrice}` : ""}</p>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                {p.recherche_dossier_id ? (
                    <Button
                    type="button"
                    variant="outline"
                      size="sm"
                    onClick={() => {
                      setDashProposalDossierId(p.recherche_dossier_id);
                      setDashProposalPane("dossier");
                    }}
                  >
                    Demande
                  </Button>
                ) : null}
                {p.vente_dossier_id ? (
                  <Button
                    type="button"
                      variant="outline"
                    size="sm"
                    onClick={() => {
                      setDashProposalDossierId(p.vente_dossier_id);
                      setDashProposalPane("dossier");
                    }}
                  >
                    Fiche
                    </Button>
                ) : null}
                {p.status === "pending" ? (
                  <>
                    <Button size="sm" className="btn-call-action" disabled={proposalActionBusy === p.proposal_id} onClick={() => respondRechercheProposal(p.proposal_id, "accept")}>Accepter</Button>
                    <Button size="sm" variant="outline" className="text-red-700" disabled={proposalActionBusy === p.proposal_id} onClick={() => respondRechercheProposal(p.proposal_id, "reject")}>Refuser</Button>
                  </>
                ) : null}
                  </div>
              </div>
          );
        })() : null}
      </DetailDialog>
      <DetailDialog
        open={Boolean(dashAnnonceDetailId)}
        onOpenChange={(open) => {
          if (!open) {
            setDashAnnonceDetailId(null);
            setDashAnnoncePane("main");
          }
        }}
        size="full"
        contentClassName="!py-0"
        testId="dash-annonce-detail-dialog"
        title="Fiche annonce"
        hideTitle
        toolbar={
          dashAnnonceDetailRow?.dossier_id
            ? (() => {
                const d = dashAnnonceDetailRow;
                const lockRaw = d?.seller_deletion_locked_until;
                const lockUntil = lockRaw ? new Date(lockRaw) : null;
                const sellerDeleteLocked =
                  lockUntil && !Number.isNaN(lockUntil.getTime()) && lockUntil.getTime() > Date.now();
                return (
                  <ModalActions className="justify-end gap-2 border-0 pt-0">
                    <Button variant="outline" size="sm" onClick={() => shareAnnonce(d)}>
                      <Share2 className="mr-1 h-4 w-4" />
                      Partager
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEditAnnonce(d)}>
                      <Pencil className="mr-1 h-4 w-4" />
                      Modifier
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-700"
                      onClick={() => deleteAnnonce(d)}
                      disabled={deletingAnnonceId === d.dossier_id || sellerDeleteLocked}
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Supprimer
                    </Button>
                  </ModalActions>
                );
              })()
            : null
        }
      >
        {dashAnnonceDetailRow?.dossier_id ? (
          <ModalDossierEmbed
            dossierId={dashAnnonceDetailRow.dossier_id}
            hideNavBack
            onBack={() => {
              setDashAnnonceDetailId(null);
              setDashAnnoncePane("main");
            }}
          />
        ) : null}
      </DetailDialog>

      <DetailDialog
        open={Boolean(dashNegDetailId)}
        onOpenChange={(open) => {
          if (!open) {
            setDashNegDetailId(null);
            setDashNegPane("main");
          }
        }}
        onBack={dashNegPane === "dossier" ? () => setDashNegPane("main") : undefined}
        size="lg"
        testId="dash-neg-detail-dialog"
        title={
          dashNegPane === "dossier"
            ? "Fiche annonce"
            : dashNegDetailRow?.dossier_title ||
              dashNegDetailRow?.dossiers?.title ||
              dashNegDetailRow?.dossier?.titre ||
              "Négociation"
        }
      >
        {dashNegPane === "dossier" && (dashNegDetailRow?.dossier_id ?? dashNegDetailRow?.dossiers?.id) ? (
          <ModalDossierEmbed
            dossierId={String(dashNegDetailRow.dossier_id ?? dashNegDetailRow.dossiers?.id)}
            onBack={() => setDashNegPane("main")}
          />
        ) : (
          renderNegotiationDetail(dashNegDetailRow, {
            onOpenDossier: () => setDashNegPane("dossier"),
          })
        )}
      </DetailDialog>
      {/* QR DIALOG - scan to call from mobile */}
      <StripePaymentDialog
        open={paymentDialog.open}
        onOpenChange={(o) =>
          setPaymentDialog((p) => {
            const nextOpen = Boolean(o);
            if (nextOpen) return { ...(p || {}), open: true };
            // reset à la fermeture pour éviter de relancer une ancienne session
            return { open: false, clientSecret: "", txId: "", dossierId: "", negId: "", initialPaymentMethods: null };
          })
        }
        clientSecret={paymentDialog.clientSecret}
        initialPaymentMethods={paymentDialog.initialPaymentMethods}
        onClientSecretChange={(cs) =>
          setPaymentDialog((p) => ({ ...(p || {}), clientSecret: String(cs || "") }))
        }
        transactionId={paymentDialog.txId}
        returnUrl={`${window.location.origin}/dashboard`}
        onSuccess={async () => {
          await load();
          goToMesAchats();
          toast.success("Paiement enregistré. Votre RDV apparaît ci-dessous : vous pouvez lancer l’appel.");
        }}
      />

      <StripePaymentDialog
        open={subscriptionPay.open}
        onOpenChange={(o) =>
          setSubscriptionPay({
            open: Boolean(o),
            clientSecret: Boolean(o) ? subscriptionPay.clientSecret : "",
            intentType: Boolean(o) ? subscriptionPay.intentType : "payment",
            amountDue: Boolean(o) ? subscriptionPay.amountDue : null,
            currency: Boolean(o) ? subscriptionPay.currency : "eur",
            plan: Boolean(o) ? subscriptionPay.plan : "monthly",
            planAmount: Boolean(o) ? subscriptionPay.planAmount : null,
            planCurrency: Boolean(o) ? subscriptionPay.planCurrency : "eur",
            nextAction: Boolean(o) ? subscriptionPay.nextAction : null,
            paymentIntentId: Boolean(o) ? subscriptionPay.paymentIntentId : "",
          })
        }
        clientSecret={subscriptionPay.clientSecret}
        intentType={subscriptionPay.intentType}
        returnUrl={`${window.location.origin}/compte`}
        title="Abonnement"
        subtitle={
          <div className="space-y-1">
            <div className="text-sm font-semibold text-slate-900">
              Abonnement :{" "}
              {subscriptionPay.planAmount != null
                ? `${(Number(subscriptionPay.planAmount) / 100).toLocaleString("fr-FR")}€/${subscriptionPay.plan === "yearly" ? "an" : "mois"}`
                : "—"}
            </div>
            <div className="text-sm font-semibold text-slate-900">
              Débit immédiat aujourd’hui :{" "}
              {subscriptionPay.amountDue != null
                ? `${(Number(subscriptionPay.amountDue) / 100).toLocaleString("fr-FR")}€`
                : "—"}
            </div>
            <div className="text-[11px] text-slate-600">
              Renouvellement automatique. Vous pourrez changer de carte à tout moment.
            </div>
          </div>
        }
        showEscrowInfo={false}
        confirmLabel="S’abonner"
        onSuccess={async (intent) => {
          const pendingSnapshot = pendingBuy;
          const planSnap = subscriptionPay.plan;
          const nextSnap = subscriptionPay.nextAction;
          const closeSubModal = () => {
            setSubscriptionPay({
              open: false,
              clientSecret: "",
              intentType: "payment",
              amountDue: null,
              currency: "eur",
              plan: "monthly",
              planAmount: null,
              planCurrency: "eur",
              nextAction: null,
              paymentIntentId: "",
            });
            setSubscribing(false);
          };
          try {
            if (nextSnap === "activate_subscription") {
              const piId = intent?.id || subscriptionPay.paymentIntentId;
              if (!piId) throw new Error("PaymentIntent manquant");
              await api.post("/subscriptions/activate", { plan: planSnap, payment_intent_id: String(piId) });
              const activeOk = await waitForActiveSubscription(api);
              closeSubModal();
              if (!activeOk) {
                setSubscriptionSyncDialog({
                  open: true,
                  plan: planSnap,
                  paymentIntentId: String(piId),
                  syncing: false,
                  pendingBuySnapshot: pendingSnapshot,
                });
                toast.info("Paiement reçu — finalisation de l’abonnement…");
                return;
              }
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Impossible de finaliser l’abonnement");
            setSubscribing(false);
            return;
          }

          closeSubModal();
          toast.success("Abonnement en cours d’activation");
          await runDashboardPendingBuyLoop(pendingSnapshot);
        }}
      />

      <AlertDialog
        open={subscriptionSyncDialog.open}
        onOpenChange={(o) => {
          if (!o) setSubscriptionSyncDialog((prev) => ({ ...prev, open: false }));
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Finalisation de l’abonnement</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Votre paiement est bien enregistré. La mise à jour du compte peut prendre quelques secondes — vous pouvez relancer la synchronisation sans payer à nouveau.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Fermer</AlertDialogCancel>
            <Button
              type="button"
              className="rounded-xl"
              onClick={() => void handleSubscriptionSyncRetry()}
              disabled={subscriptionSyncDialog.syncing}
            >
              {subscriptionSyncDialog.syncing ? "Synchronisation…" : "Réessayer"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SubscriptionRequiredPurchaseModal
        open={subscribeDialog.open}
        onOpenChange={(o) => {
          if (!o) setSubscribeDialog({ open: false, plan: "monthly", dossierId: "", negId: "" });
        }}
        subscribing={subscribing}
        onSelectPlan={(plan) => void startSubscribe(plan)}
      />

      <Dialog
        modal
        open={Boolean(proposalChoice?.open)}
        onOpenChange={(v) => {
          if (!v) setProposalChoice(null);
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="proposal-choice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Proposition acceptée</DialogTitle>
            <DialogDescription>
              Que veux-tu faire maintenant ?
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 pt-2">
            <Button
              type="button"
              className="btn-primary"
              disabled={!proposalChoice?.venteId}
              onClick={() => {
                const venteId = proposalChoice?.venteId;
                const proposalId = proposalChoice?.proposalId;
                setProposalChoice(null);
                if (proposalId) setDashProposalId(String(proposalId));
                if (venteId) {
                  setDashProposalDossierId(venteId);
                  setDashProposalPane("dossier");
                }
              }}
              data-testid="proposal-choice-buy"
            >
              Acheter la fiche (paiement)
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                const negId = proposalChoice?.negotiationId;
                setProposalChoice(null);
                await refreshNegotiations();
                if (negId) {
                  setDashNegPane("main");
                  setDashNegDetailId(String(negId));
                } else {
                goToNegotiations();
                }
              }}
              data-testid="proposal-choice-negotiate"
            >
              Ouvrir la négociation
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProposalChoice(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {inlineCallTx && inlineCallTx.transaction_id != null && String(inlineCallTx.transaction_id).trim() !== ""
        ? createPortal(
        <div className={RDM_CALL_SHELL_CLASS} data-testid="inline-call-dialog">
          <button
            type="button"
            className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
            aria-label="Fermer l’appel"
                onClick={() => void closeInlineCall()}
          />
          <div
            role="dialog"
            aria-modal="true"
                className="pointer-events-auto relative z-10 w-full max-w-lg max-h-[92vh] overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-[#F9FAFB] p-2 shadow-[0_30px_90px_-35px_rgba(2,6,23,0.35)] sm:p-3"
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1 z-20 h-9 w-9 rounded-full text-slate-600 hover:bg-white/80"
              aria-label="Fermer"
                  onClick={() => void closeInlineCall()}
            >
              <X className="h-4 w-4" />
            </Button>
            <BuyerCallSession
              key={`${String(inlineCallTx.transaction_id).trim()}-${inlineCallOpenOutcome ? "outcome" : "call"}-${inlineCallForceRetry ? "retry" : "new"}`}
              transactionId={String(inlineCallTx.transaction_id).trim()}
              variant="embedded"
              openOutcomeOnMount={inlineCallOpenOutcome}
              forceRetry={inlineCallForceRetry}
                  onRequestExit={() => void closeInlineCall()}
              onRequestOpenDispute={({ transactionId: tid, defaultDescription }) => {
                    void closeInlineCall();
                const row = buys.find((b) => normTxId(b.transaction_id ?? b.id) === normTxId(tid));
                if (!row) {
                  toast.error("Ligne d’achat introuvable. Rechargez le tableau de bord puis réessayez.");
                  void load();
                  return;
                }
                if (isDisputeOpeningBlockedByPlatform(row)) {
                  toast.error(OPEN_DISPUTE_BLOCKED_PLATFORM_DETAIL_FR);
                  void load();
                  return;
                }
                setShowDispute(row);
                setDisputeReason("autre");
                setDisputeDesc(defaultDescription || "");
              }}
            />
          </div>
            </div>,
            document.body,
          )
        : null}

      {/* RATING DIALOG */}
      <Dialog open={!!editingAnnonce} onOpenChange={(v) => !v && !savingAnnonce && setEditingAnnonce(null)}>
        <DialogContent
          data-testid="edit-annonce-dialog"
          className="flex max-h-[85vh] w-[95vw] max-w-2xl flex-col gap-0 overflow-hidden p-0"
        >
          <DialogHeader className="border-b border-slate-100 px-6 py-4">
            <DialogTitle className="font-display">Modifier l’annonce</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-6 py-4">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Select
                value={editAnnonceForm.type || "vente"}
                onValueChange={(v) => setEditAnnonceForm((p) => ({ ...p, type: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Type d’annonce" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vente">Ce que je vends</SelectItem>
                  <SelectItem value="recherche">Ce que j’achète</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={editAnnonceForm.secteur || ""}
                onValueChange={(v) =>
                  setEditAnnonceForm((p) => ({ ...p, secteur: v, produit_id: "", custom_fields: {} }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Secteur" />
                </SelectTrigger>
                <SelectContent>
                  {editSecteurs.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Select
              value={editAnnonceForm.produit_id || ""}
              onValueChange={(v) => setEditAnnonceForm((p) => ({ ...p, produit_id: v, custom_fields: {} }))}
              disabled={!editAnnonceForm.secteur}
            >
              <SelectTrigger>
                <SelectValue placeholder={editAnnonceForm.secteur ? "Produit" : "Choisir d’abord le secteur"} />
              </SelectTrigger>
              <SelectContent>
                {editProduitsFiltres.map((p) => (
                  <SelectItem key={p.produit_id} value={p.produit_id}>{p.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {editChampsDynamiques.length > 0 && (
              <div className="space-y-2 rounded-xl border border-slate-200/80 bg-slate-50/60 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Champs de qualification</p>
                {editChampsDynamiques.map((c) => {
                  const key = String(c.nom);
                  const value = editAnnonceForm.custom_fields?.[key];
                  const listOptions = Array.isArray(c.options) ? c.options.map((o) => String(o).trim()).filter(Boolean) : [];
                  return (
                    <div key={c.champ_id ?? key} className="space-y-1.5">
                      <p className="text-sm font-medium text-slate-800">
                        {key}
                        {c.visible_public === false ? (
                          <span className="ml-1.5 text-[11px] font-semibold text-amber-700">(masqué marketplace)</span>
                        ) : null}
                      </p>
                      {c.type === "list" ? (
                        <Select
                          value={value != null && value !== "" ? String(value) : ""}
                          onValueChange={(v) =>
                            setEditAnnonceForm((p) => ({
                              ...p,
                              custom_fields: { ...(p.custom_fields || {}), [key]: v },
                            }))
                          }
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Choisir" />
                          </SelectTrigger>
                          <SelectContent>
                            {listOptions.map((o) => (
                              <SelectItem key={o} value={o}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : c.type === "boolean" ? (
                        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <Switch
                            checked={Boolean(value)}
                            onCheckedChange={(v) =>
                              setEditAnnonceForm((p) => ({
                                ...p,
                                custom_fields: { ...(p.custom_fields || {}), [key]: v },
                              }))
                            }
                          />
                          <span className="text-sm text-slate-700">{value ? "Oui" : "Non"}</span>
                        </div>
                      ) : c.type === "number" && isPhoneLikeChampName(key) ? (
                        <Input
                          type="tel"
                          inputMode="tel"
                          value={value ?? ""}
                          onChange={(e) =>
                            setEditAnnonceForm((p) => ({
                              ...p,
                              custom_fields: {
                                ...(p.custom_fields || {}),
                                [key]: formatPhoneInputLive(e.target.value),
                              },
                            }))
                          }
                          className="bg-white font-mono tabular-nums"
                        />
                      ) : (
                        <Input
                          type="text"
                          inputMode={c.type === "number" ? "decimal" : undefined}
                          value={value ?? ""}
                          onChange={(e) =>
                            setEditAnnonceForm((p) => ({
                              ...p,
                              custom_fields: { ...(p.custom_fields || {}), [key]: e.target.value },
                            }))
                          }
                          className={c.type === "number" ? "bg-white font-mono tabular-nums" : "bg-white"}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {editChampsMasquesRecherche.length > 0 && (
              <div className="rounded-xl border border-amber-200/80 bg-amber-50/70 px-3 py-2.5 text-xs text-amber-900">
                Champs masqués (non remplissables en recherche) : {editChampsMasquesRecherche.join(", ")}
              </div>
            )}
            <Input
              value={editAnnonceForm.title}
              onChange={(e) => setEditAnnonceForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Titre"
            />
            <Textarea
              value={editAnnonceForm.description}
              onChange={(e) => setEditAnnonceForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Description"
              rows={4}
            />
            {normalizeAnnonceType(editAnnonceForm.type) === "recherche" ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Input
                  type="text"
                  inputMode="decimal"
                  value={editAnnonceForm.price_min}
                  onChange={(e) => setEditAnnonceForm((p) => ({ ...p, price_min: e.target.value }))}
                  placeholder="Budget min (€)"
                  className="font-mono tabular-nums"
                />
                <Input
                  type="text"
                  inputMode="decimal"
                  value={editAnnonceForm.price_max}
                  onChange={(e) => setEditAnnonceForm((p) => ({ ...p, price_max: e.target.value }))}
                  placeholder="Budget max (€)"
                  className="font-mono tabular-nums"
                />
              </div>
            ) : (
              <Input
                type="text"
                inputMode="decimal"
                value={editAnnonceForm.price}
                onChange={(e) => setEditAnnonceForm((p) => ({ ...p, price: e.target.value }))}
                placeholder="Prix (€)"
                className="font-mono tabular-nums"
              />
            )}
            {normalizeAnnonceType(editAnnonceForm.type) === "recherche" && editDeptChampKey ? (
              <div className="space-y-1">
              <Input
                value={editAnnonceForm.custom_fields?.[editDeptChampKey] ?? ""}
                onChange={(e) =>
                  setEditAnnonceForm((p) => ({
                    ...p,
                    custom_fields: { ...(p.custom_fields || {}), [editDeptChampKey]: e.target.value },
                  }))
                }
                  placeholder="Départements : 63, 43, 03 ou 63-43-03"
                className="font-mono tabular-nums"
                  data-testid="edit-annonce-departements"
              />
                <p className="text-[11px] text-slate-500">
                  Plusieurs départements possibles, séparés par des virgules, espaces ou tirets.
                </p>
              </div>
            ) : null}
            {/* En recherche, le département suffit dans la plupart des cas : éviter 2 lignes similaires. */}
            {normalizeAnnonceType(editAnnonceForm.type) !== "recherche" || !editDeptChampKey ? (
              <Input
                value={editAnnonceForm.location}
                onChange={(e) => setEditAnnonceForm((p) => ({ ...p, location: e.target.value }))}
                placeholder="Localisation"
              />
            ) : null}
            {normalizeAnnonceType(editAnnonceForm.type) !== "recherche" ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                value={editAnnonceForm.client_name}
                onChange={(e) => setEditAnnonceForm((p) => ({ ...p, client_name: e.target.value }))}
                placeholder="Nom du client/prospect"
              />
              <Input
                type="tel"
                inputMode="tel"
                value={editAnnonceForm.client_phone}
                onChange={(e) =>
                  setEditAnnonceForm((p) => ({ ...p, client_phone: formatPhoneInputLive(e.target.value) }))
                }
                placeholder="Téléphone du client/prospect"
                className="font-mono tabular-nums"
              />
            </div>
            ) : null}
          </div>
          <DialogFooter className="border-t border-slate-200/80 px-6 py-4">
            <Button variant="outline" onClick={() => setEditingAnnonce(null)} disabled={savingAnnonce}>Annuler</Button>
            <Button className="btn-primary" onClick={saveAnnonceEdits} disabled={savingAnnonce}>
              {savingAnnonce ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RATING DIALOG */}
      <Dialog open={!!showRating} onOpenChange={(v) => !v && setShowRating(null)}>
        <DialogContent data-testid="rating-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {showRating && user?.user_id && String(showRating.buyer_id || "") === String(user.user_id)
                ? "Évaluer le vendeur"
                : "Évaluer l’acheteur"}
            </DialogTitle>
            <DialogDescription className="text-left text-sm text-slate-600">
              {showRating && user?.user_id && String(showRating.buyer_id || "") === String(user.user_id)
                ? "Après votre achat, une note sur 5 suffit — pas de commentaire texte."
                : "Après cette vente, une note sur 5 sur votre expérience avec cet acheteur — pas de commentaire texte."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <RatingStars value={ratingValue} onChange={setRatingValue} size="lg" />
            <p className="text-xs text-slate-500 text-center">Note uniquement — pas de commentaire.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRating(null)} disabled={submittingRating}>
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={submitRating}
              disabled={!ratingValue || submittingRating}
              data-testid="submit-rating"
            >
              {submittingRating ? "Envoi…" : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!verdictNoticeDialog} onOpenChange={(v) => !v && setVerdictNoticeDialog(null)}>
        <DialogContent data-testid="verdict-notice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Décision du litige</DialogTitle>
            <DialogDescription className="text-left text-sm text-slate-700 whitespace-pre-wrap">
              {verdictNoticeDialog?.headline}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 text-sm text-slate-900 whitespace-pre-wrap border border-slate-200 rounded-md p-3 bg-slate-50/80">
            {verdictNoticeDialog?.message ? (
              verdictNoticeDialog.message
            ) : (
              <span className="text-slate-500">Aucun message détaillé de la part de l’administrateur.</span>
            )}
          </div>
          <DialogFooter>
            <Button className="btn-primary" onClick={() => setVerdictNoticeDialog(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DISPUTE DIALOG */}
      <Dialog open={!!showDispute} onOpenChange={(v) => !v && setShowDispute(null)}>
        <DialogContent data-testid="dispute-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Ouvrir un litige</DialogTitle>
            <DialogDescription className="text-left text-sm text-slate-600">
              Réservé aux achats pour lesquels vous avez déjà <strong>confirmé ou refusé le RDV</strong> (étape « Décision requise »
              terminée). Ouvrez un litige si vous contestez le déroulé ou la décision — de préférence{" "}
              <strong>dans les 72 heures</strong> suivant cette clôture. Décrivez le problème avec précision : un admin examine sous
              quelques jours. Le versement au vendeur est suspendu pendant l’instruction ; après clôture en faveur du vendeur, un
              délai de {SELLER_PAYOUT_HOURS} h peut s’appliquer avant encaissement.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Select value={disputeReason} onValueChange={setDisputeReason}>
              <SelectTrigger data-testid="dispute-reason-select"><SelectValue placeholder="Motif du litige" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dossier_faux">Dossier faux / inexistant</SelectItem>
                <SelectItem value="injoignable">Client injoignable</SelectItem>
                <SelectItem value="pas_interessse">Pas intéressé / faux profil</SelectItem>
                <SelectItem value="deja_sollicite">Déjà sollicité par un autre acheteur</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Description détaillée" value={disputeDesc} onChange={(e) => setDisputeDesc(e.target.value)} rows={4} data-testid="dispute-description" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDispute(null)}>Annuler</Button>
            <Button className="btn-primary" onClick={openDispute} disabled={!disputeReason || !disputeDesc} data-testid="submit-dispute">Ouvrir le litige</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE NEGOTIATION CONFIRMATION */}
      <Dialog
        open={deleteNegDialog.open}
        onOpenChange={(v) => (!v ? setDeleteNegDialog({ open: false, neg: null }) : null)}
      >
        <DialogContent data-testid="delete-negotiation-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Supprimer la négociation ?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700">
            Cette action est définitive. Voulez-vous vraiment supprimer cette négociation ?
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteNegDialog({ open: false, neg: null })}
              disabled={Boolean(deletingNegId)}
            >
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDeleteNegotiation}
              disabled={Boolean(deletingNegId)}
              data-testid="confirm-delete-negotiation"
            >
              {deletingNegId ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const EmptyState = ({ title, label, cta }) => (
  <div className="rounded-2xl border border-dashed border-slate-200/90 bg-gradient-to-b from-slate-50/80 to-white px-6 py-12 text-center sm:py-14">
    {title ? <h3 className="font-display text-lg font-bold text-[#0B0D12]">{title}</h3> : null}
    <p className={cn("mx-auto max-w-md text-sm leading-relaxed text-slate-600", title && "mt-2", cta && "mb-6")}>
      {label}
    </p>
    {cta}
  </div>
);

export default Dashboard;
